import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams, Link, useOutletContext } from 'react-router-dom';
import { 
    Check, ChevronRight, ChevronLeft, Save, Upload, Video, Image as ImageIcon, 
    Globe, Layers, Info, Eye, CheckCircle2, AlertCircle, Trash2, Star, 
    Play, Pause, Sliders, Scissors, FileText, ArrowRight, ArrowLeft, RefreshCw,
    Plus, Package, ShieldCheck, Tag, DollarSign, Truck, Sparkles, X, Rocket,
    Box, Compass, CheckCircle, Smartphone, Monitor, Camera, Film, Clapperboard,
    Radio, MoveLeft, MoveRight
} from 'lucide-react';
import { 
    getCategories, 
    getActiveCountries, 
    uploadMedia, 
    createProduct,
    updateProduct,
    getProduct,
    getUserId,
    resolveStorefrontImageUrl
} from '../api/client';
import { isRTL } from '../utils/translations';
import Spinner from '../components/Spinner';
import './seller.css';

const DRAFT_STORAGE_KEY = 'luxvono_seller_active_draft_v2';

// ── Preset Luxury Colors (matching Mobile App) ────────────────────────
const PRESET_COLORS = [
    { name: 'Black', nameAr: 'أسود ملكي', nameSo: 'Madow', hex: '#000000' },
    { name: 'White', nameAr: 'أبيض لؤلؤي', nameSo: 'Cad', hex: '#FFFFFF' },
    { name: 'Navy Blue', nameAr: 'أزرق كحلي', nameSo: 'Buluug Madow', hex: '#0A1F44' },
    { name: 'Royal Blue', nameAr: 'أزرق ملكي', nameSo: 'Buluug Boqortooyo', hex: '#1E5EFF' },
    { name: 'Crimson Red', nameAr: 'أحمر قاني', nameSo: 'Gaduud', hex: '#DC2626' },
    { name: 'Emerald Green', nameAr: 'أخضر زمردي', nameSo: 'Cagaar', hex: '#059669' },
    { name: 'Royal Gold', nameAr: 'ذهبي ملكي', nameSo: 'Dahabi', hex: '#D97706' },
    { name: 'Silver / Slate', nameAr: 'فضي / رمادي', nameSo: 'Qalin', hex: '#64748B' },
    { name: 'Deep Purple', nameAr: 'بنفسجي داكن', nameSo: 'Basali', hex: '#6B21A8' },
    { name: 'Burgundy', nameAr: 'عنابي فاخر', nameSo: 'Burgundy', hex: '#831843' }
];

// ── Preset Sizes Categories (matching Mobile App) ─────────────────────
const SIZE_PRESETS = {
    CLOTHING: {
        label: 'ملابس وأزياء (Clothing)',
        sizes: ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL']
    },
    SHOES: {
        label: 'أحذية (Shoes)',
        sizes: ['EU 38', 'EU 39', 'EU 40', 'EU 41', 'EU 42', 'EU 43', 'EU 44', 'EU 45', 'EU 46']
    },
    TECH: {
        label: 'أجهزة وسعات تخزين (Storage)',
        sizes: ['64GB', '128GB', '256GB', '512GB', '1TB']
    },
    PERFUME: {
        label: 'عطور وسوائل (Volume)',
        sizes: ['50ml', '75ml', '100ml', '150ml', '200ml', 'تولة (12ml)']
    }
};

// ── Default Categories with Emojis ──────────────────────────────────
const DEFAULT_STUDIO_CATEGORIES = [
    { id: 1, name: 'ساعات ومجوهرات فاخرة', nameAr: 'ساعات ومجوهرات فاخرة', nameEn: 'Luxury Watches & Jewelry', icon: '💎', subcategories: ['ساعات رجالية', 'ساعات نسائية', 'مجوهرات وألماس', 'إكسسوارات'] },
    { id: 2, name: 'حقائب وإكسسوارات جلدية', nameAr: 'حقائب وإكسسوارات جلدية', nameEn: 'Bags & Leather Accessories', icon: '👜', subcategories: ['حقائب يد', 'محافظ جلدية', 'أحزمة', 'حقائب سفر'] },
    { id: 3, name: 'أزياء وموضة حصرية', nameAr: 'أزياء وموضة حصرية', nameEn: 'Exclusive Fashion', icon: '✨', subcategories: ['ملابس رجالية', 'فساتين وعبايات', 'أزياء موسمية', 'أحذية راقية'] },
    { id: 4, name: 'إلكترونيات وأجهزة ذكية', nameAr: 'إلكترونيات وأجهزة ذكية', nameEn: 'Electronics & Smart Devices', icon: '📱', subcategories: ['هواتف ذكية', 'ساعات ذكية', 'سماعات وصوتيات', 'أجهزة لوحية'] },
    { id: 5, name: 'عطور ومنتجات عناية', nameAr: 'عطور ومنتجات عناية', nameEn: 'Perfumes & Personal Care', icon: '💄', subcategories: ['عطور شرقية', 'عطور فرنسية', 'بخور وعود', 'عناية فاخرة'] },
    { id: 6, name: 'تشكيلات ومنتجات عامة', nameAr: 'تشكيلات ومنتجات عامة', nameEn: 'General Luxury Goods', icon: '🛍️', subcategories: ['عام', 'مجموعات حصرية', 'هدايا فاخرة'] }
];

// ── Target Markets Catalog ──────────────────────────────────────────
const TARGET_MARKETS_CATALOG = [
    { code: 'SA', name: 'المملكة العربية السعودية', flag: '🇸🇦' },
    { code: 'AE', name: 'الإمارات العربية المتحدة', flag: '🇦🇪' },
    { code: 'SO', name: 'جمهورية الصومال', flag: '🇸🇴' },
    { code: 'KW', name: 'دولة الكويت', flag: '🇰🇼' },
    { code: 'QA', name: 'دولة قطر', flag: '🇶🇦' },
    { code: 'BH', name: 'مملكة البحرين', flag: '🇧🇭' },
    { code: 'OM', name: 'سلطنة عُمان', flag: '🇴🇲' },
    { code: 'US', name: 'الولايات المتحدة', flag: '🇺🇸' },
    { code: 'GB', name: 'المملكة المتحدة', flag: '🇬🇧' }
];

export default function AddProductWizard() {
    const navigate = useNavigate();
    const outletCtx = useOutletContext();
    const isApprovedVendor = outletCtx?.isApprovedVendor;
    const [searchParams] = useSearchParams();
    const editProductId = searchParams.get('edit');
    const isResumeDraft = searchParams.get('resume') === 'true';
    const rtl = isRTL();

    useEffect(() => {
        if (isApprovedVendor === false) {
            navigate('/vendor/apply');
        }
    }, [isApprovedVendor, navigate]);

    // ── 6 Streamlined Stages ───────────────────────────────────────────
    const [currentStep, setCurrentStep] = useState(1);
    const [categories, setCategories] = useState(DEFAULT_STUDIO_CATEGORIES);
    const [countries, setCountries] = useState(TARGET_MARKETS_CATALOG);
    const [loadingInit, setLoadingInit] = useState(true);

    // Mobile App UI state
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
    const activeChipRef = useRef(null);
    const [draftToast, setDraftToast] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (activeChipRef.current) {
            try {
                activeChipRef.current.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
            } catch (_) {}
        }
    }, [currentStep]);

    const handleSaveDraft = () => {
        try {
            localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(formData));
            setDraftToast(true);
            setTimeout(() => setDraftToast(false), 3000);
        } catch (_) {}
    };

    // Multi-Language Active Tab
    const [activeLangTab, setActiveLangTab] = useState('ar'); // 'ar', 'en', 'so'

    // Form State (Aligned with Mobile App's 6-Stage Studio)
    const [formData, setFormData] = useState({
        // Stage 1: Category & Market
        categoryId: '1',
        categoryName: 'ساعات ومجوهرات فاخرة',
        subcategory: 'ساعات رجالية',
        targetMarkets: ['SA', 'AE', 'SO'],
        isWorldwideMarket: false,
        audience: 'RETAIL', // 'RETAIL' | 'WHOLESALE' | 'BOTH'

        // Stage 2: Basic Info & Localization
        nameAr: '',
        nameEn: '',
        nameSo: '',
        shortDescriptionAr: '',
        shortDescriptionEn: '',
        shortDescriptionSo: '',
        descriptionAr: '',
        descriptionEn: '',
        descriptionSo: '',
        brand: '',
        model: '',
        sku: 'LX-' + Math.floor(100000 + Math.random() * 900000),
        condition: 'NEW', // 'NEW' | 'USED' | 'REFURBISHED'

        // Stage 3: Media Studio
        coverImageUrl: '',
        images: [], // array of string URLs
        videoUrl: '', // Primary showcase reel
        videos: [], // array of string URLs (Multiple Reels & Videos)
        pinToActiveLiveStream: false,

        // Stage 4: Colors, Sizes & Variants Matrix
        selectedColors: [
            { name: 'Black', nameAr: 'أسود ملكي', hex: '#000000', imageUrl: '' },
            { name: 'Royal Gold', nameAr: 'ذهبي ملكي', hex: '#D97706', imageUrl: '' }
        ],
        selectedSizes: ['S', 'M', 'L'],
        sizeType: 'CLOTHING',
        customColorName: '',
        customColorHex: '#1E5EFF',
        customSizeInput: '',
        variants: [], // [{ id, sku, colorName, colorHex, sizeName, price, wholesalePrice, stock, lowStockThreshold, isAvailable, imageUrl }]
        bulkStockVal: '',
        bulkPriceVal: '',
        bulkSkuPrefix: '',

        // Stage 5: Pricing, Inventory & Logistics
        price: '2500',
        compareAtPrice: '3200',
        currency: 'SAR',
        wholesalePrice: '1800',
        moq: '10',
        unitsPerCarton: '24',
        masterStock: '50',
        lowStockThreshold: '5',
        weight: '0.8',
        weightUnit: 'kg',
        length: '20',
        width: '15',
        height: '10',
        dimensionUnit: 'cm',
        shippingClass: 'STANDARD' // 'STANDARD' | 'VIP_EXPRESS' | 'HEAVY' | 'TEMPERATURE_CONTROLLED'
    });

    // Upload & UI Status
    const [uploadingCover, setUploadingCover] = useState(false);
    const [uploadingGallery, setUploadingGallery] = useState(false);
    const [uploadingVideo, setUploadingVideo] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadStatusText, setUploadStatusText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submittedProductId, setSubmittedProductId] = useState(null);
    const [errorMsg, setErrorMsg] = useState(null);
    const [recoveredDraft, setRecoveredDraft] = useState(false);
    const [mediaActiveTab, setMediaActiveTab] = useState('ALL'); // 'ALL' | 'PHOTOS' | 'VIDEOS'
    const [isDraggingMedia, setIsDraggingMedia] = useState(false);

    const coverInputRef = useRef(null);
    const coverCameraInputRef = useRef(null);
    const galleryInputRef = useRef(null);
    const galleryCameraInputRef = useRef(null);
    const videoInputRef = useRef(null);
    const videoCameraInputRef = useRef(null);
    const multiMediaInputRef = useRef(null);

    // ── Load Initial Categories & Data ────────────────────────────────
    useEffect(() => {
        const init = async () => {
            setLoadingInit(true);
            try {
                const [cats, cntrs] = await Promise.all([
                    getCategories().catch(() => []),
                    getActiveCountries().catch(() => [])
                ]);

                if (Array.isArray(cats) && cats.length > 0) {
                    setCategories(cats.map(c => ({
                        id: c.id,
                        name: c.name || c.nameAr,
                        nameAr: c.nameAr || c.name,
                        nameEn: c.nameEn || c.name,
                        icon: c.iconEmoji || (c.id === 1 ? '💎' : c.id === 2 ? '👜' : c.id === 3 ? '✨' : c.id === 4 ? '📱' : c.id === 5 ? '💄' : '🛍️'),
                        subcategories: c.subcategories || ['عام', 'حصرية']
                    })));
                }

                if (Array.isArray(cntrs) && cntrs.length > 0) {
                    setCountries(cntrs);
                }

                // If editing existing product
                if (editProductId) {
                    const prodRes = await getProduct(editProductId);
                    if (prodRes && (prodRes.data || prodRes.id)) {
                        const p = prodRes.data || prodRes;
                        setFormData(prev => ({
                            ...prev,
                            nameAr: p.nameAr || p.name || '',
                            nameEn: p.nameEn || p.name || '',
                            nameSo: p.nameSo || '',
                            shortDescriptionAr: p.shortDescriptionAr || p.shortDescription || '',
                            shortDescriptionEn: p.shortDescriptionEn || p.shortDescription || '',
                            descriptionAr: p.descriptionAr || p.description || '',
                            descriptionEn: p.descriptionEn || p.description || '',
                            price: String(p.price || ''),
                            compareAtPrice: String(p.compareAtPrice || ''),
                            currency: p.currency || 'SAR',
                            wholesalePrice: String(p.wholesalePrice || ''),
                            moq: String(p.moq || '1'),
                            masterStock: String(p.quantity || '50'),
                            brand: p.brand || '',
                            model: p.model || '',
                            sku: p.sku || prev.sku,
                            categoryId: String(p.categoryId || '1'),
                            categoryName: p.categoryName || prev.categoryName,
                            coverImageUrl: p.primaryImageUrl || p.imageUrl || '',
                            images: Array.isArray(p.images) ? p.images : [],
                            videoUrl: p.videoUrl || '',
                            targetMarkets: Array.isArray(p.availableCountries) && p.availableCountries.length > 0 ? p.availableCountries : ['SA'],
                            audience: p.audience || 'RETAIL',
                            condition: p.condition || 'NEW'
                        }));
                    }
                } else {
                    // Check LocalStorage Draft
                    const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
                    if (savedDraft) {
                        try {
                            const parsed = JSON.parse(savedDraft);
                            if (parsed && (parsed.nameAr || parsed.coverImageUrl || parsed.price)) {
                                if (isResumeDraft || window.confirm(rtl ? 'يوجد مسودة غير مكتملة محفوظة مسبقاً. هل تود استرجاعها؟' : 'An unsaved draft exists. Would you like to resume?')) {
                                    setFormData(prev => ({ ...prev, ...parsed }));
                                    setRecoveredDraft(true);
                                }
                            }
                        } catch (e) {}
                    }
                }
            } catch (err) {
                console.error('Initialization error:', err);
            } finally {
                setLoadingInit(false);
            }
        };

        init();
    }, [editProductId, isResumeDraft, rtl]);

    // ── Auto-save Draft to LocalStorage ────────────────────────────────
    useEffect(() => {
        if (!editProductId && (formData.nameAr || formData.coverImageUrl || formData.price)) {
            const timeout = setTimeout(() => {
                localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(formData));
            }, 1200);
            return () => clearTimeout(timeout);
        }
    }, [formData, editProductId]);

    // ── Auto Generate Variant Matrix when Colors/Sizes Change ─────────
    const generateVariantMatrix = () => {
        const colors = formData.selectedColors.length > 0 
            ? formData.selectedColors 
            : [{ name: 'Standard', nameAr: 'قياسي', hex: '#000000', imageUrl: '' }];
        const sizes = formData.selectedSizes.length > 0 
            ? formData.selectedSizes 
            : ['Standard'];

        const basePrice = formData.price || '0';
        const baseWholesale = formData.wholesalePrice || '';
        const baseSku = formData.sku || 'LX-' + Math.floor(100000 + Math.random() * 900000);

        const newMatrix = [];
        colors.forEach((color, cIdx) => {
            sizes.forEach((size, sIdx) => {
                const cCode = (color.name || color.hex).replace('#', '').substring(0, 3).toUpperCase();
                const sCode = size.replace(/[^A-Za-z0-9]/g, '').toUpperCase() || 'STD';
                const vSku = `${baseSku}-${cCode}-${sCode}`;

                newMatrix.push({
                    id: `${cIdx}-${sIdx}-${Date.now()}`,
                    colorName: color.nameAr || color.name,
                    colorHex: color.hex,
                    sizeName: size,
                    sku: vSku,
                    price: basePrice,
                    wholesalePrice: baseWholesale,
                    stock: 15,
                    lowStockThreshold: 5,
                    isAvailable: true,
                    imageUrl: color.imageUrl || formData.coverImageUrl || ''
                });
            });
        });

        setFormData(prev => ({ ...prev, variants: newMatrix }));
    };

    // Auto generate on first load if variants empty
    useEffect(() => {
        if (formData.variants.length === 0 && (formData.selectedColors.length > 0 || formData.selectedSizes.length > 0)) {
            generateVariantMatrix();
        }
    }, []);

    // ── Media Upload Handlers (Enhanced Multi-Media Studio) ──────────
    const handleBatchMediaUpload = async (files) => {
        const fileList = Array.from(files || []).filter(Boolean);
        if (fileList.length === 0) return;

        const imageFiles = [];
        const videoFiles = [];

        fileList.forEach(file => {
            const isVideo = file.type?.startsWith('video/') || /\.(mp4|mov|webm|m4v|mkv|avi|quicktime)$/i.test(file.name || '');
            if (isVideo) {
                videoFiles.push(file);
            } else {
                imageFiles.push(file);
            }
        });

        setUploadingGallery(true);
        const totalCount = fileList.length;
        setUploadStatusText(
            rtl 
                ? `جاري رفع ومعالجة ${totalCount} وسائط (${imageFiles.length} صور، ${videoFiles.length} فيديو)...` 
                : `Uploading & processing ${totalCount} media items (${imageFiles.length} photos, ${videoFiles.length} videos)...`
        );
        setErrorMsg(null);

        try {
            const res = await uploadMedia(fileList, (prog) => {
                const percent = prog.total ? Math.round((prog.loaded * 100) / prog.total) : 50;
                setUploadProgress(percent);
            }, 'AUTO');

            const extractedImages = [];
            const extractedVideos = [];

            const processItem = (item) => {
                const u = item?.url || item?.imageUrl || item?.videoUrl || (typeof item === 'string' ? item : null);
                if (typeof u === 'string' && u) {
                    const finalUrl = u.startsWith('/') ? `https://luxvono.com${u}` : u;
                    const isVideo = item?.isVideo || /\.(mp4|webm|mov|m4v|m3u8|mkv)/i.test(finalUrl);
                    if (isVideo) {
                        extractedVideos.push(finalUrl);
                    } else {
                        extractedImages.push(finalUrl);
                    }
                }
            };

            if (Array.isArray(res)) {
                res.forEach(processItem);
            } else if (res?.data && Array.isArray(res.data)) {
                res.data.forEach(processItem);
            } else if (res && typeof res === 'object') {
                processItem(res);
            }

            setFormData(prev => {
                const updatedImages = [...new Set([...prev.images, ...extractedImages])];
                const prevVideos = Array.isArray(prev.videos) ? prev.videos : (prev.videoUrl ? [prev.videoUrl] : []);
                const updatedVideos = [...new Set([...prevVideos, ...extractedVideos])];
                const primaryCover = prev.coverImageUrl || updatedImages[0] || '';
                const primaryVideo = prev.videoUrl || updatedVideos[0] || '';

                return {
                    ...prev,
                    coverImageUrl: primaryCover,
                    images: updatedImages,
                    videoUrl: primaryVideo,
                    videos: updatedVideos
                };
            });
        } catch (err) {
            console.error('Batch media upload failed:', err);
            setErrorMsg(rtl ? 'فشل رفع بعض الوسائط. تأكد من حجم الملفات والاتصال بالإنترنت.' : 'Failed to upload some media.');
        } finally {
            setUploadingGallery(false);
            setUploadStatusText('');
            if (multiMediaInputRef.current) multiMediaInputRef.current.value = '';
            if (galleryInputRef.current) galleryInputRef.current.value = '';
            if (videoInputRef.current) videoInputRef.current.value = '';
        }
    };

    const handleCoverUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploadingCover(true);
        setUploadStatusText(rtl ? 'جاري رفع صورة الغلاف...' : 'Uploading cover image...');
        setErrorMsg(null);

        try {
            const res = await uploadMedia([file], (prog) => {
                const percent = prog.total ? Math.round((prog.loaded * 100) / prog.total) : 50;
                setUploadProgress(percent);
            }, 'AUTO');

            let url = '';
            if (Array.isArray(res)) {
                url = res[0]?.url || res[0]?.imageUrl || res[0] || '';
            } else if (res && typeof res === 'object') {
                url = res.url || res.imageUrl || (Array.isArray(res.data) ? res.data[0]?.url : '');
            }

            if (url) {
                const finalUrl = url.startsWith('/') ? `https://luxvono.com${url}` : url;
                setFormData(prev => ({
                    ...prev,
                    coverImageUrl: finalUrl,
                    images: prev.images.includes(finalUrl) ? prev.images : [finalUrl, ...prev.images]
                }));
            } else {
                throw new Error(rtl ? 'فشل استخراج رابط الصورة من الخادم' : 'Failed to retrieve uploaded image URL');
            }
        } catch (err) {
            console.error('Cover upload failed:', err);
            setErrorMsg(rtl ? 'فشل رفع صورة الغلاف. تأكد من حجم الصورة وصيغتها.' : 'Failed to upload cover image.');
        } finally {
            setUploadingCover(false);
            setUploadStatusText('');
            if (coverInputRef.current) coverInputRef.current.value = '';
            if (coverCameraInputRef.current) coverCameraInputRef.current.value = '';
        }
    };

    const handleGalleryUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        setUploadingGallery(true);
        setUploadStatusText(rtl ? `جاري رفع ${files.length} صور للمعرض...` : `Uploading ${files.length} gallery images...`);
        setErrorMsg(null);

        try {
            const res = await uploadMedia(files, (prog) => {
                const percent = prog.total ? Math.round((prog.loaded * 100) / prog.total) : 50;
                setUploadProgress(percent);
            }, 'AUTO');

            const newUrls = [];
            const collectUrl = (item) => {
                const u = item?.url || item?.imageUrl || (typeof item === 'string' ? item : null);
                if (typeof u === 'string' && u) newUrls.push(u.startsWith('/') ? `https://luxvono.com${u}` : u);
            };

            if (Array.isArray(res)) {
                res.forEach(collectUrl);
            } else if (res?.data && Array.isArray(res.data)) {
                res.data.forEach(collectUrl);
            }

            if (newUrls.length > 0) {
                setFormData(prev => ({
                    ...prev,
                    coverImageUrl: prev.coverImageUrl || newUrls[0],
                    images: [...new Set([...prev.images, ...newUrls])]
                }));
            }
        } catch (err) {
            console.error('Gallery upload failed:', err);
            setErrorMsg(rtl ? 'فشل رفع بعض صور المعرض.' : 'Failed to upload gallery images.');
        } finally {
            setUploadingGallery(false);
            setUploadStatusText('');
            if (galleryInputRef.current) galleryInputRef.current.value = '';
            if (galleryCameraInputRef.current) galleryCameraInputRef.current.value = '';
        }
    };

    const handleVideoUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        setUploadingVideo(true);
        setUploadStatusText(rtl ? `جاري رفع ومعالجة ${files.length} فيديوهات للمنتج...` : `Uploading ${files.length} product videos...`);
        setErrorMsg(null);

        try {
            const res = await uploadMedia(files, (prog) => {
                const percent = prog.total ? Math.round((prog.loaded * 100) / prog.total) : 50;
                setUploadProgress(percent);
            }, 'FASTSTART');

            const newVideoUrls = [];
            const collectVideo = (item) => {
                const u = item?.url || item?.videoUrl || (typeof item === 'string' ? item : null);
                if (typeof u === 'string' && u) newVideoUrls.push(u.startsWith('/') ? `https://luxvono.com${u}` : u);
            };

            if (Array.isArray(res)) {
                res.forEach(collectVideo);
            } else if (res?.data && Array.isArray(res.data)) {
                res.data.forEach(collectVideo);
            } else if (res && typeof res === 'object') {
                collectVideo(res);
            }

            if (newVideoUrls.length > 0) {
                setFormData(prev => {
                    const prevVids = Array.isArray(prev.videos) ? prev.videos : (prev.videoUrl ? [prev.videoUrl] : []);
                    const combined = [...new Set([...prevVids, ...newVideoUrls])];
                    return {
                        ...prev,
                        videoUrl: prev.videoUrl || newVideoUrls[0],
                        videos: combined
                    };
                });
            }
        } catch (err) {
            console.error('Video upload failed:', err);
            setErrorMsg(rtl ? 'فشل رفع الفيديو. يرجى التأكد من أن صيغة الملف MP4 أو WebM أو MOV.' : 'Failed to upload video.');
        } finally {
            setUploadingVideo(false);
            setUploadStatusText('');
            if (videoInputRef.current) videoInputRef.current.value = '';
            if (videoCameraInputRef.current) videoCameraInputRef.current.value = '';
        }
    };

    const handleSetAsCover = (url) => {
        setFormData(prev => ({ ...prev, coverImageUrl: url }));
    };

    const handleSetAsPrimaryVideo = (url) => {
        setFormData(prev => ({ ...prev, videoUrl: url }));
    };

    const handleMoveImage = (fromIdx, toIdx) => {
        setFormData(prev => {
            if (toIdx < 0 || toIdx >= prev.images.length) return prev;
            const updated = [...prev.images];
            const [moved] = updated.splice(fromIdx, 1);
            updated.splice(toIdx, 0, moved);
            return { ...prev, images: updated };
        });
    };

    const handleRemoveImage = (idx) => {
        setFormData(prev => {
            const removedUrl = prev.images[idx];
            const updated = prev.images.filter((_, i) => i !== idx);
            let nextCover = prev.coverImageUrl;
            if (nextCover === removedUrl) {
                nextCover = updated[0] || '';
            }
            return { ...prev, images: updated, coverImageUrl: nextCover };
        });
    };

    const handleRemoveVideo = (idx) => {
        setFormData(prev => {
            const prevVideos = Array.isArray(prev.videos) ? prev.videos : (prev.videoUrl ? [prev.videoUrl] : []);
            const removedUrl = prevVideos[idx];
            const updated = prevVideos.filter((_, i) => i !== idx);
            let nextPrimary = prev.videoUrl;
            if (nextPrimary === removedUrl) {
                nextPrimary = updated[0] || '';
            }
            return { ...prev, videos: updated, videoUrl: nextPrimary };
        });
    };


    // ── Step Navigation & Validation ──────────────────────────────────
    const validateStep = (step) => {
        setErrorMsg(null);
        if (step === 1) {
            if (!formData.categoryId) {
                setErrorMsg(rtl ? 'يرجى اختيار فئة المنتج.' : 'Please select a product category.');
                return false;
            }
            if (!formData.isWorldwideMarket && formData.targetMarkets.length === 0) {
                setErrorMsg(rtl ? 'يرجى اختيار سوق مستهدف واحد على الأقل أو تفعيل البيع عالمياً.' : 'Please select at least one target market or enable Worldwide.');
                return false;
            }
        }
        if (step === 2) {
            if (!formData.nameAr.trim() && !formData.nameEn.trim()) {
                setErrorMsg(rtl ? 'يرجى كتابة اسم المنتج بالعربية أو الإنجليزية.' : 'Please enter the product name.');
                return false;
            }
        }
        if (step === 3) {
            if (!formData.coverImageUrl && formData.images.length === 0) {
                setErrorMsg(rtl ? 'يرجى رفع صورة الغلاف للمنتج لعرضه في المتجر.' : 'Please upload a cover image for the product.');
                return false;
            }
        }
        if (step === 5) {
            if (!formData.price || parseFloat(formData.price) <= 0) {
                setErrorMsg(rtl ? 'يرجى إدخال سعر صحيح للمنتج.' : 'Please enter a valid product price.');
                return false;
            }
        }
        return true;
    };

    const handleNext = () => {
        if (validateStep(currentStep)) {
            setCurrentStep(prev => Math.min(6, prev + 1));
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleBack = () => {
        setCurrentStep(prev => Math.max(1, prev - 1));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // ── Final Submission for Review (Stage 6) ─────────────────────────
    const handleSubmitForReview = async () => {
        if (isApprovedVendor === false) {
            alert(rtl ? 'عذراً، نشر المنتجات متاح فقط للتجار المعتمدين بعد موافقة الإدارة الرسمية.' : 'Publishing products is restricted to approved vendors only.');
            navigate('/vendor/apply');
            return;
        }

        if (!validateStep(1) || !validateStep(2) || !validateStep(3) || !validateStep(5)) {
            return;
        }

        setIsSubmitting(true);
        setErrorMsg(null);
        setUploadStatusText(rtl ? 'جاري اعتماد ونشر المنشور للمراجعة...' : 'Submitting product for review...');

        try {
            const finalCover = formData.coverImageUrl || formData.images[0] || '';
            const allImages = formData.images.length > 0 ? formData.images : [finalCover].filter(Boolean);

            const isWorldwide = formData.isWorldwideMarket || formData.targetMarkets.includes('WORLDWIDE') || formData.targetMarkets.includes('ALL');
            const primaryMarket = isWorldwide ? 'SA' : (formData.targetMarkets[0] || 'SA');

            const payload = {
                name: (formData.nameAr || formData.nameEn || 'Luxvono Product').trim(),
                nameAr: formData.nameAr?.trim() || formData.nameEn?.trim() || '',
                nameEn: formData.nameEn?.trim() || formData.nameAr?.trim() || '',
                nameSo: formData.nameSo?.trim() || '',
                shortDescription: formData.shortDescriptionAr || formData.shortDescriptionEn || '',
                shortDescriptionAr: formData.shortDescriptionAr || formData.shortDescriptionEn || '',
                shortDescriptionEn: formData.shortDescriptionEn || formData.shortDescriptionAr || '',
                shortDescriptionSo: formData.shortDescriptionSo || '',
                description: formData.descriptionAr || formData.descriptionEn || '',
                descriptionAr: formData.descriptionAr || formData.descriptionEn || '',
                descriptionEn: formData.descriptionEn || formData.descriptionAr || '',
                descriptionSo: formData.descriptionSo || '',
                brand: formData.brand || undefined,
                model: formData.model || undefined,
                sku: formData.sku?.trim() || `LX-${Date.now()}`,
                condition: formData.condition || 'NEW',
                categoryId: parseInt(formData.categoryId) || 1,
                categoryName: formData.categoryName || 'General',
                countryCode: primaryMarket,
                availableCountries: isWorldwide ? ['WORLDWIDE'] : formData.targetMarkets,
                targetMarkets: isWorldwide ? ['WORLDWIDE'] : formData.targetMarkets,
                audience: formData.audience || 'RETAIL',
                price: parseFloat(formData.price) || 0,
                compareAtPrice: formData.compareAtPrice ? parseFloat(formData.compareAtPrice) : null,
                currency: formData.currency || 'SAR',
                wholesalePrice: formData.wholesalePrice ? parseFloat(formData.wholesalePrice) : null,
                moq: parseInt(formData.moq) || 1,
                unitsPerCarton: formData.unitsPerCarton ? parseInt(formData.unitsPerCarton) : null,
                quantity: parseInt(formData.masterStock) || 50,
                weight: formData.weight ? parseFloat(formData.weight) : 0.8,
                weightUnit: formData.weightUnit || 'kg',
                length: formData.length ? parseFloat(formData.length) : null,
                width: formData.width ? parseFloat(formData.width) : null,
                height: formData.height ? parseFloat(formData.height) : null,
                dimensionUnit: formData.dimensionUnit || 'cm',
                shippingClass: formData.shippingClass || 'STANDARD',
                imageUrl: finalCover,
                primaryImageUrl: finalCover,
                images: allImages,
                videoUrl: formData.videoUrl || undefined,
                videos: formData.videos && formData.videos.length > 0 ? formData.videos : (formData.videoUrl ? [formData.videoUrl] : []),
                active: true, // Will be set to pending review by moderation engine
                status: 'PENDING_REVIEW',
                approvalStatus: 'PENDING_REVIEW',
                variants: formData.variants.map((v, idx) => ({
                    sku: v.sku || `${formData.sku || 'LX'}-${idx + 1}`,
                    price: parseFloat(v.price) || parseFloat(formData.price) || 0,
                    wholesalePrice: v.wholesalePrice ? parseFloat(v.wholesalePrice) : null,
                    quantity: parseInt(v.stock) || 10,
                    lowStockThreshold: parseInt(v.lowStockThreshold) || 5,
                    colorHex: v.colorHex || '#000000',
                    colorName: v.colorName || 'Default',
                    sizeName: v.sizeName || 'Standard',
                    imageUrl: v.imageUrl || finalCover,
                    isAvailable: v.isAvailable !== false
                }))
            };

            let res;
            if (editProductId) {
                res = await updateProduct(editProductId, payload);
            } else {
                res = await createProduct(payload);
            }

            // Clear draft upon successful submission
            localStorage.removeItem(DRAFT_STORAGE_KEY);
            setSubmittedProductId(res?.data?.id || res?.id || editProductId || 'OK');
            setSubmitSuccess(true);
        } catch (err) {
            console.error('Submission failed:', err);
            setErrorMsg(err?.response?.data?.message || err?.message || (rtl ? 'فشل إرسال المنتج للمراجعة. يرجى التحقق من المدخلات.' : 'Submission failed.'));
        } finally {
            setIsSubmitting(false);
            setUploadStatusText('');
        }
    };

    const clearDraftAndReset = () => {
        if (window.confirm(rtl ? 'هل أنت متأكد من رغبتك في حذف المسودة والبدء من جديد؟' : 'Are you sure you want to discard draft and reset?')) {
            localStorage.removeItem(DRAFT_STORAGE_KEY);
            window.location.reload();
        }
    };

    if (loadingInit) {
        return (
            <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
                <Spinner />
                <p style={{ color: '#94a3b8', fontSize: 14 }}>{rtl ? 'جاري تحميل استوديو النشر الفاخر...' : 'Loading Publishing Studio...'}</p>
            </div>
        );
    }

    // ── Celebration Success Screen (Phase 20 Mobile Alignment) ─────────
    if (submitSuccess) {
        return (
            <div style={{ maxWidth: 680, margin: '40px auto', padding: '0 16px' }}>
                <div style={{
                    background: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: 24,
                    padding: '44px 32px',
                    textAlign: 'center',
                    boxShadow: '0 20px 50px rgba(0, 0, 0, 0.06)'
                }}>
                    <div style={{
                        width: 84,
                        height: 84,
                        borderRadius: '50%',
                        background: '#ECFDF5',
                        border: '2px solid #10B981',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 24px auto',
                        boxShadow: '0 0 30px rgba(16, 185, 129, 0.2)'
                    }}>
                        <CheckCircle2 size={46} color="#10B981" />
                    </div>

                    <h2 style={{ fontSize: 24, fontWeight: 800, color: '#111827', margin: '0 0 12px 0' }}>
                        {rtl ? 'تم إرسال المنشور للمراجعة والاعتماد! ✓' : 'Product Submitted for Review ✓'}
                    </h2>

                    <p style={{ fontSize: 14.5, color: '#4B5563', lineHeight: 1.7, margin: '0 auto 28px auto', maxWidth: 520 }}>
                        {rtl 
                            ? 'تم إدراج منشورك وجميع خياراته وصوره بنجاح لدى فريق جودة ومنصة مراجعة لوكشفونو. ستتلقى إشعاراً فورياً على هاتفك وحسابك بمجرد قبول المنشور ونشره للمشترين.'
                            : 'Your luxury product and variants have been submitted for moderation. You will receive an instant push notification once approved and published.'}
                    </p>

                    {/* Product Summary Preview Card */}
                    <div style={{
                        background: '#F9FAFB',
                        border: '1px solid #E5E7EB',
                        borderRadius: 16,
                        padding: 20,
                        marginBottom: 32,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 18,
                        textAlign: rtl ? 'right' : 'left'
                    }}>
                        {formData.coverImageUrl ? (
                            <img 
                                src={resolveStorefrontImageUrl(formData.coverImageUrl)} 
                                alt="Cover" 
                                style={{ width: 76, height: 76, borderRadius: 12, objectFit: 'cover', border: '1px solid #E5E7EB' }} 
                            />
                        ) : (
                            <div style={{ width: 76, height: 76, borderRadius: 12, background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Package size={32} color="#9CA3AF" />
                            </div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#FEF3C7', color: '#B45309', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, marginBottom: 6 }}>
                                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#F59E0B' }} />
                                {rtl ? 'قيد مراجعة الإدارة (PENDING_REVIEW)' : 'PENDING REVIEW'}
                            </div>
                            <h4 style={{ margin: '0 0 4px 0', fontSize: 16, color: '#111827', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {formData.nameAr || formData.nameEn || 'Luxury Product'}
                            </h4>
                            <div style={{ fontSize: 14, color: '#FE2C55', fontWeight: 800 }}>
                                {formData.price} {formData.currency}
                                {formData.compareAtPrice && (
                                    <span style={{ fontSize: 12, color: '#9CA3AF', textDecoration: 'line-through', marginInlineStart: 8 }}>
                                        {formData.compareAtPrice} {formData.currency}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Quick Buttons */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <button
                            onClick={() => navigate('/seller/products')}
                            style={{
                                width: '100%',
                                padding: '14px 24px',
                                borderRadius: 12,
                                background: 'linear-gradient(135deg, #FE2C55 0%, #E01E45 100%)',
                                color: '#ffffff',
                                border: 'none',
                                fontWeight: 700,
                                fontSize: 15,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 8,
                                boxShadow: '0 8px 24px rgba(254, 44, 85, 0.3)'
                            }}
                        >
                            <Package size={18} />
                            <span>{rtl ? 'عرض منتجاتي في لوحة البائع' : 'View Products in Seller Hub'}</span>
                        </button>

                        <button
                            onClick={() => {
                                setSubmitSuccess(false);
                                setCurrentStep(1);
                                setFormData(prev => ({
                                    ...prev,
                                    nameAr: '',
                                    nameEn: '',
                                    nameSo: '',
                                    coverImageUrl: '',
                                    images: [],
                                    videoUrl: '',
                                    sku: 'LX-' + Math.floor(100000 + Math.random() * 900000)
                                }));
                            }}
                            style={{
                                width: '100%',
                                padding: '13px 24px',
                                borderRadius: 12,
                                background: '#F9FAFB',
                                color: '#374151',
                                border: '1px solid #E5E7EB',
                                fontWeight: 600,
                                fontSize: 14,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 8
                            }}
                        >
                            <Plus size={16} />
                            <span>{rtl ? 'إضافة منشور آخر جديد +' : 'Add Another Product +'}</span>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ── 6 Stages Meta ─────────────────────────────────────────────────
    const STAGES = [
        { id: 1, titleAr: 'الفئة والأسواق', titleEn: 'Category & Markets', icon: Globe },
        { id: 2, titleAr: 'بيانات المنتج', titleEn: 'Product Details', icon: FileText },
        { id: 3, titleAr: 'استوديو الوسائط', titleEn: 'Media Studio', icon: ImageIcon },
        { id: 4, titleAr: 'الألوان والمقاسات', titleEn: 'Colors & Variants', icon: Sliders },
        { id: 5, titleAr: 'الأسعار والمخزون', titleEn: 'Pricing & Stock', icon: DollarSign },
        { id: 6, titleAr: 'المعاينة والاعتماد', titleEn: 'Review & Publish', icon: Rocket }
    ];

    return (
        <div className="wizard-outer-container" style={{ direction: rtl ? 'rtl' : 'ltr' }}>
            
            {/* ── Native Mobile App Header & Stepper (< 768px) ── */}
            {isMobile ? (
                <>
                    <div className="mobile-app-header-bar">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <button
                                type="button"
                                onClick={() => navigate('/seller/products')}
                                className="mobile-header-back-btn"
                                aria-label="Back"
                            >
                                {rtl ? <ArrowRight size={19} /> : <ArrowLeft size={19} />}
                            </button>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span className="mobile-header-title">
                                        {editProductId ? (rtl ? 'تعديل المنشور' : 'Edit') : (rtl ? 'استوديو النشر الفاخر' : 'Luxury Studio')}
                                    </span>
                                    <span className="mobile-step-counter">
                                        {rtl ? `مرحلة ${currentStep}/6` : `${currentStep}/6`}
                                    </span>
                                </div>
                                <div className="mobile-step-name">
                                    {rtl ? STAGES[currentStep - 1].titleAr : STAGES[currentStep - 1].titleEn}
                                </div>
                            </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <button
                                type="button"
                                onClick={clearDraftAndReset}
                                className="mobile-header-action-btn"
                                title={rtl ? 'بدء من جديد' : 'Clear Draft'}
                            >
                                <Trash2 size={16} color="#f87171" />
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveDraft}
                                className="mobile-header-action-btn primary"
                                title={rtl ? 'حفظ مسودة' : 'Save Draft'}
                            >
                                <Save size={15} color="#FE2C55" />
                                <span style={{ fontSize: 11, fontWeight: 700, color: '#FE2C55' }}>{rtl ? 'حفظ' : 'Save'}</span>
                            </button>
                        </div>
                    </div>

                    {/* Mobile Stepper Carousel (Scrollable Pills + Progress Track) */}
                    <div className="mobile-stepper-carousel">
                        <div className="mobile-progress-track">
                            <div 
                                className="mobile-progress-fill" 
                                style={{ width: `${(currentStep / 6) * 100}%` }}
                            />
                        </div>

                        <div className="mobile-pills-row">
                            {STAGES.map((st) => {
                                const IconComponent = st.icon;
                                const isCompleted = currentStep > st.id;
                                const isActive = currentStep === st.id;

                                return (
                                    <button
                                        key={st.id}
                                        ref={isActive ? activeChipRef : null}
                                        type="button"
                                        onClick={() => (isCompleted || isActive) && setCurrentStep(st.id)}
                                        className={`mobile-stage-pill ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                                    >
                                        <div className="mobile-pill-icon-wrap">
                                            {isCompleted ? (
                                                <Check size={12} strokeWidth={3} />
                                            ) : (
                                                <IconComponent size={13} />
                                            )}
                                        </div>
                                        <span>{rtl ? st.titleAr : st.titleEn}</span>
                                        {isActive && (
                                            <span className="mobile-pill-badge">{st.id}</span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </>
            ) : (
                /* ── Desktop Professional Header & 6-Grid Stepper (>= 768px) ── */
                <>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 14 }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                                <span style={{ background: '#FFF0F3', color: '#FE2C55', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
                                    {rtl ? 'استوديو النشر الفاخر 🚀' : 'LUXVONO Creator Studio 🚀'}
                                </span>
                                {recoveredDraft && (
                                    <span style={{ background: 'rgba(16, 185, 129, 0.12)', color: '#059669', padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                                        {rtl ? 'تم استرجاع المسودة ✓' : 'Draft Restored'}
                                    </span>
                                )}
                            </div>
                            <h1 style={{ fontSize: 24, fontWeight: 800, color: 'var(--seller-text, #111827)', margin: 0 }}>
                                {editProductId ? (rtl ? 'تعديل المنشور والمنتج' : 'Edit Product') : (rtl ? 'إضافة منشور ومنتج جديد' : 'Publish New Product')}
                            </h1>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <button
                                onClick={clearDraftAndReset}
                                style={{
                                    background: '#FFF0F3',
                                    border: '1px solid #FECDD3',
                                    color: '#FE2C55',
                                    padding: '8px 14px',
                                    borderRadius: 10,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6
                                }}
                            >
                                <Trash2 size={15} />
                                <span>{rtl ? 'بدء من جديد' : 'Clear Draft'}</span>
                            </button>
                            
                            <button
                                onClick={() => navigate('/seller/products')}
                                style={{
                                    background: '#F9FAFB',
                                    border: '1px solid #E5E7EB',
                                    color: '#6B7280',
                                    padding: '8px 16px',
                                    borderRadius: 10,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    cursor: 'pointer'
                                }}
                            >
                                {rtl ? 'إلغاء والعودة' : 'Cancel'}
                            </button>
                        </div>
                    </div>

                    {/* Desktop 6-Stage Stepper Navigation Bar */}
                    <div style={{
                        background: 'var(--seller-card, #ffffff)',
                        border: '1px solid var(--seller-border, #e5e7eb)',
                        borderRadius: 18,
                        padding: '16px 20px',
                        marginBottom: 28,
                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.04)'
                    }}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(6, 1fr)',
                            gap: 8,
                            alignItems: 'center'
                        }}>
                            {STAGES.map((st) => {
                                const IconComponent = st.icon;
                                const isCompleted = currentStep > st.id;
                                const isActive = currentStep === st.id;

                                return (
                                    <div 
                                        key={st.id}
                                        onClick={() => isCompleted && setCurrentStep(st.id)}
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            cursor: isCompleted ? 'pointer' : 'default',
                                            opacity: isActive ? 1 : isCompleted ? 0.95 : 0.45,
                                            transition: 'all 0.2s ease',
                                            position: 'relative'
                                        }}
                                    >
                                        <div style={{
                                            width: 38,
                                            height: 38,
                                            borderRadius: '50%',
                                            background: isActive 
                                                ? '#FE2C55' 
                                                : isCompleted 
                                                    ? '#10B981' 
                                                    : '#F3F4F6',
                                            color: isActive || isCompleted ? '#ffffff' : '#9CA3AF',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontWeight: 700,
                                            fontSize: 14,
                                            marginBottom: 6,
                                            boxShadow: isActive ? '0 4px 14px rgba(254, 44, 85, 0.4)' : 'none',
                                            border: isActive ? '2px solid #FE2C55' : isCompleted ? '1px solid #10B981' : '1px solid #E5E7EB'
                                        }}>
                                            {isCompleted ? <Check size={18} strokeWidth={3} /> : <IconComponent size={18} />}
                                        </div>
                                        <span style={{
                                            fontSize: 11.5,
                                            fontWeight: isActive ? 800 : 600,
                                            color: isActive ? '#FE2C55' : isCompleted ? '#059669' : '#6B7280',
                                            textAlign: 'center',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: '100%'
                                        }}>
                                            {rtl ? st.titleAr : st.titleEn}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Progress Line */}
                        <div style={{ height: 4, background: '#F3F4F6', borderRadius: 2, marginTop: 14, overflow: 'hidden' }}>
                            <div style={{
                                height: '100%',
                                width: `${((currentStep - 1) / 5) * 100}%`,
                                background: 'linear-gradient(90deg, #FE2C55, #E11D48, #10B981)',
                                transition: 'width 0.3s ease'
                            }} />
                        </div>
                    </div>
                </>
            )}

            {/* Floating Luxury Draft Toast */}
            {draftToast && (
                <div className="luxury-toast-notification">
                    <CheckCircle2 size={18} color="#34d399" />
                    <span>{rtl ? 'تم حفظ المسودة بنجاح! يمكنك استئناف العمل لاحقاً.' : 'Draft saved successfully!'}</span>
                </div>
            )}

            {/* Error Message Toast */}
            {errorMsg && (
                <div style={{
                    background: 'rgba(239, 68, 68, 0.15)',
                    border: '1px solid rgba(239, 68, 68, 0.4)',
                    color: '#fca5a5',
                    borderRadius: 14,
                    padding: '14px 18px',
                    marginBottom: 24,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    fontSize: 14,
                    fontWeight: 600
                }}>
                    <AlertCircle size={20} color="#ef4444" style={{ flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>{errorMsg}</span>
                    <button onClick={() => setErrorMsg(null)} style={{ background: 'none', border: 'none', color: '#fca5a5', cursor: 'pointer' }}>
                        <X size={18} />
                    </button>
                </div>
            )}

            {/* ═════════════════════════════════════════════════════════════
                STAGE 1: CATEGORY & TARGET MARKETS (الفئة والأسواق)
               ═════════════════════════════════════════════════════════════ */}
            {currentStep === 1 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                    
                    {/* Category Selection */}
                    <div style={{
                        background: 'var(--seller-card, #ffffff)',
                        border: '1px solid var(--seller-border, #e5e7eb)',
                        borderRadius: 18,
                        padding: 24,
                        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.03)'
                    }}>
                        <h3 style={{ fontSize: 17, fontWeight: 800, color: 'var(--seller-text, #111827)', margin: '0 0 6px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span>💎</span>
                            <span>{rtl ? 'اختر فئة المنتج الرئيسية' : 'Select Product Category'}</span>
                        </h3>
                        <p style={{ fontSize: 13, color: 'var(--seller-text-muted, #6b7280)', margin: '0 0 18px 0' }}>
                            {rtl ? 'حدد الفئة المناسبة لضمان ظهور منتجك في محركات البحث والتصنيفات الدقيقة للمشترين.' : 'Pick the best category for targeted luxury discovery.'}
                        </p>

                        <div className="wizard-category-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
                            {categories.map((cat) => {
                                const isSelected = String(formData.categoryId) === String(cat.id);
                                return (
                                    <div
                                        key={cat.id}
                                        onClick={() => setFormData(prev => ({
                                            ...prev,
                                            categoryId: String(cat.id),
                                            categoryName: cat.nameAr || cat.name,
                                            subcategory: cat.subcategories?.[0] || 'عام'
                                        }))}
                                        style={{
                                            background: isSelected ? '#FFF0F3' : '#F9FAFB',
                                            border: isSelected ? '2px solid #FE2C55' : '1px solid #E5E7EB',
                                            borderRadius: 14,
                                            padding: '16px 14px',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 12,
                                            boxShadow: isSelected ? '0 4px 14px rgba(254, 44, 85, 0.15)' : 'none'
                                        }}
                                    >
                                        <div style={{ fontSize: 26 }}>{cat.icon || '🛍️'}</div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: 14, fontWeight: isSelected ? 800 : 600, color: isSelected ? '#FE2C55' : '#111827' }}>
                                                {rtl ? cat.nameAr || cat.name : cat.nameEn || cat.name}
                                            </div>
                                        </div>
                                        {isSelected && <Check size={18} color="#FE2C55" strokeWidth={2.5} />}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Target Markets Selection */}
                    <div style={{
                        background: 'var(--seller-card, #ffffff)',
                        border: '1px solid var(--seller-border, #e5e7eb)',
                        borderRadius: 18,
                        padding: 24,
                        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.03)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
                            <div>
                                <h3 style={{ fontSize: 17, fontWeight: 800, color: 'var(--seller-text, #111827)', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Globe size={18} color="#FE2C55" />
                                    <span>{rtl ? 'الأسواق المستهدفة وتوفر المنتج' : 'Target Markets & Availability'}</span>
                                </h3>
                                <p style={{ fontSize: 13, color: 'var(--seller-text-muted, #6b7280)', margin: 0 }}>
                                    {rtl ? 'اختر الدول التي يتوفر فيها الشحن أو حدد البيع عالمياً لكافة الأسواق.' : 'Select countries where this item can be shipped or sold.'}
                                </p>
                            </div>

                            {/* Worldwide Button */}
                            <button
                                type="button"
                                onClick={() => setFormData(prev => ({
                                    ...prev,
                                    isWorldwideMarket: !prev.isWorldwideMarket,
                                    targetMarkets: !prev.isWorldwideMarket ? ['WORLDWIDE'] : ['SA', 'AE', 'SO']
                                }))}
                                style={{
                                    background: formData.isWorldwideMarket ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)' : '#F9FAFB',
                                    color: formData.isWorldwideMarket ? '#ffffff' : '#374151',
                                    border: formData.isWorldwideMarket ? '1px solid #059669' : '1px solid #E5E7EB',
                                    borderRadius: 12,
                                    padding: '9px 18px',
                                    fontSize: 13,
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    boxShadow: formData.isWorldwideMarket ? '0 4px 16px rgba(16, 185, 129, 0.35)' : 'none'
                                }}
                            >
                                <span>🌍</span>
                                <span>{rtl ? 'متاح لجميع الأسواق عالمياً' : 'Sell Worldwide'}</span>
                                {formData.isWorldwideMarket && <Check size={16} strokeWidth={2.5} />}
                            </button>
                        </div>

                        {!formData.isWorldwideMarket && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 16 }}>
                                {countries.map((cnt) => {
                                    const code = cnt.code || cnt.countryCode;
                                    const isSelected = formData.targetMarkets.includes(code);
                                    return (
                                        <button
                                            type="button"
                                            key={code}
                                            onClick={() => {
                                                setFormData(prev => {
                                                    const exists = prev.targetMarkets.includes(code);
                                                    const next = exists 
                                                        ? prev.targetMarkets.filter(c => c !== code)
                                                        : [...prev.targetMarkets, code];
                                                    return { ...prev, targetMarkets: next.length > 0 ? next : [code] };
                                                });
                                            }}
                                            style={{
                                                background: isSelected ? '#FFF0F3' : '#F9FAFB',
                                                border: isSelected ? '1px solid #FE2C55' : '1px solid #E5E7EB',
                                                color: isSelected ? '#FE2C55' : '#374151',
                                                padding: '8px 16px',
                                                borderRadius: 10,
                                                fontSize: 13,
                                                fontWeight: isSelected ? 700 : 600,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8,
                                                transition: 'all 0.15s ease'
                                            }}
                                        >
                                            <span>{cnt.flag || '🌐'}</span>
                                            <span>{cnt.name || cnt.countryName || code}</span>
                                            {isSelected && <Check size={14} strokeWidth={2.5} />}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Target Audience */}
                    <div style={{
                        background: 'var(--seller-card, #ffffff)',
                        border: '1px solid var(--seller-border, #e5e7eb)',
                        borderRadius: 18,
                        padding: 24,
                        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.03)'
                    }}>
                        <h3 style={{ fontSize: 17, fontWeight: 800, color: 'var(--seller-text, #111827)', margin: '0 0 14px 0' }}>
                            {rtl ? 'نوع الجمهور المستهدف' : 'Target Customer Audience'}
                        </h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                            {[
                                { key: 'RETAIL', titleAr: 'مستهلكين وتجزئة (Retail)', titleEn: 'Retail Consumers', descAr: 'بيع للمتسوقين والأفراد مباشرة' },
                                { key: 'WHOLESALE', titleAr: 'تجار وجملة (B2B Wholesale)', titleEn: 'B2B Wholesale Only', descAr: 'بيع بالكرتون وبكميات محددة' },
                                { key: 'BOTH', titleAr: 'كلاهما (تجزئة وجملة معا)', titleEn: 'Retail & Wholesale', descAr: 'سعر مخصص للقطاعي وسعر مخصص للجملة' }
                            ].map(item => {
                                const isSelected = formData.audience === item.key;
                                return (
                                    <div
                                        key={item.key}
                                        onClick={() => setFormData(prev => ({ ...prev, audience: item.key }))}
                                        style={{
                                            background: isSelected ? '#FFF0F3' : '#F9FAFB',
                                            border: isSelected ? '2px solid #FE2C55' : '1px solid #E5E7EB',
                                            borderRadius: 14,
                                            padding: 16,
                                            cursor: 'pointer',
                                            transition: 'all 0.15s ease'
                                        }}
                                    >
                                        <div style={{ fontWeight: 700, fontSize: 14, color: isSelected ? '#FE2C55' : '#111827', marginBottom: 4 }}>
                                            {rtl ? item.titleAr : item.titleEn}
                                        </div>
                                        <div style={{ fontSize: 12, color: '#6B7280' }}>{item.descAr}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* ═════════════════════════════════════════════════════════════
                STAGE 2: PRODUCT DETAILS & LOCALIZATION (بيانات المنتج)
               ═════════════════════════════════════════════════════════════ */}
            {currentStep === 2 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                    
                    {/* Multi-Language Tabs */}
                    <div style={{
                        background: 'var(--seller-card, #ffffff)',
                        border: '1px solid var(--seller-border, #e5e7eb)',
                        borderRadius: 18,
                        padding: 24,
                        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.03)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
                            <h3 style={{ fontSize: 17, fontWeight: 800, color: 'var(--seller-text, #111827)', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <FileText size={18} color="#FE2C55" />
                                <span>{rtl ? 'تفاصيل المنتج باللغات المدعومة' : 'Product Names & Descriptions'}</span>
                            </h3>

                            {/* Language Switcher Pills */}
                            <div style={{ display: 'flex', gap: 6, background: '#F3F4F6', padding: 4, borderRadius: 12, border: '1px solid #E5E7EB' }}>
                                {[
                                    { key: 'ar', label: '🇸🇦 العربية (أساسي)' },
                                    { key: 'en', label: '🇬🇧 English' },
                                    { key: 'so', label: '🇸🇴 Soomaali' }
                                ].map(tab => (
                                    <button
                                        type="button"
                                        key={tab.key}
                                        onClick={() => setActiveLangTab(tab.key)}
                                        style={{
                                            background: activeLangTab === tab.key ? '#FE2C55' : 'transparent',
                                            color: activeLangTab === tab.key ? '#ffffff' : '#4B5563',
                                            border: 'none',
                                            borderRadius: 8,
                                            padding: '6px 14px',
                                            fontSize: 12.5,
                                            fontWeight: 700,
                                            cursor: 'pointer',
                                            transition: 'all 0.15s ease'
                                        }}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Arabic Fields */}
                        {activeLangTab === 'ar' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--seller-text, #111827)', marginBottom: 6 }}>
                                        {rtl ? 'اسم المنتج بالعربية *' : 'Product Name (Arabic) *'}
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="مثال: ساعة رولكس دايتونا ذهب عيار 18 إصدار حصري"
                                        value={formData.nameAr}
                                        onChange={e => setFormData(prev => ({ ...prev, nameAr: e.target.value }))}
                                        style={{
                                            width: '100%',
                                            padding: '12px 16px',
                                            borderRadius: 12,
                                            background: '#ffffff',
                                            border: '1px solid #D1D5DB',
                                            color: '#111827',
                                            fontSize: 14,
                                            outline: 'none',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--seller-text, #111827)', marginBottom: 6 }}>
                                        {rtl ? 'الوصف المختصر (يظهر أسفل السعر)' : 'Short Description'}
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="مثال: مقاومة للماء حتى عمق 100 متر، ضمان دولي معتمد 5 سنوات"
                                        value={formData.shortDescriptionAr}
                                        onChange={e => setFormData(prev => ({ ...prev, shortDescriptionAr: e.target.value }))}
                                        style={{
                                            width: '100%',
                                            padding: '12px 16px',
                                            borderRadius: 12,
                                            background: '#ffffff',
                                            border: '1px solid #D1D5DB',
                                            color: '#111827',
                                            fontSize: 14,
                                            outline: 'none',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--seller-text, #111827)', marginBottom: 6 }}>
                                        {rtl ? 'الوصف الكامل والمواصفات' : 'Full Detailed Description'}
                                    </label>
                                    <textarea
                                        rows={4}
                                        placeholder="اكتب مواصفات المنتج بالتفصيل، المواد المصنعة، المزايا الفاخرة..."
                                        value={formData.descriptionAr}
                                        onChange={e => setFormData(prev => ({ ...prev, descriptionAr: e.target.value }))}
                                        style={{
                                            width: '100%',
                                            padding: '12px 16px',
                                            borderRadius: 12,
                                            background: '#ffffff',
                                            border: '1px solid #D1D5DB',
                                            color: '#111827',
                                            fontSize: 14,
                                            outline: 'none',
                                            boxSizing: 'border-box',
                                            resize: 'vertical'
                                        }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* English Fields */}
                        {activeLangTab === 'en' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--seller-text, #111827)', marginBottom: 6 }}>
                                        Product Name (English)
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="e.g. Rolex Cosmograph Daytona 18k Everose Gold"
                                        value={formData.nameEn}
                                        onChange={e => setFormData(prev => ({ ...prev, nameEn: e.target.value }))}
                                        style={{
                                            width: '100%',
                                            padding: '12px 16px',
                                            borderRadius: 12,
                                            background: '#ffffff',
                                            border: '1px solid #D1D5DB',
                                            color: '#111827',
                                            fontSize: 14,
                                            outline: 'none',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--seller-text, #111827)', marginBottom: 6 }}>
                                        Short Description (English)
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="e.g. Certified 100m waterproof, 5-year luxury warranty"
                                        value={formData.shortDescriptionEn}
                                        onChange={e => setFormData(prev => ({ ...prev, shortDescriptionEn: e.target.value }))}
                                        style={{
                                            width: '100%',
                                            padding: '12px 16px',
                                            borderRadius: 12,
                                            background: '#ffffff',
                                            border: '1px solid #D1D5DB',
                                            color: '#111827',
                                            fontSize: 14,
                                            outline: 'none',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--seller-text, #111827)', marginBottom: 6 }}>
                                        Full Description (English)
                                    </label>
                                    <textarea
                                        rows={4}
                                        placeholder="Enter detailed English product specifications..."
                                        value={formData.descriptionEn}
                                        onChange={e => setFormData(prev => ({ ...prev, descriptionEn: e.target.value }))}
                                        style={{
                                            width: '100%',
                                            padding: '12px 16px',
                                            borderRadius: 12,
                                            background: '#ffffff',
                                            border: '1px solid #D1D5DB',
                                            color: '#111827',
                                            fontSize: 14,
                                            outline: 'none',
                                            boxSizing: 'border-box',
                                            resize: 'vertical'
                                        }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Somali Fields */}
                        {activeLangTab === 'so' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--seller-text, #111827)', marginBottom: 6 }}>
                                        Magaca Alaabta (Af-Soomaali)
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Tusaale: Saacad Rolex Dahab ah oo heer sare ah"
                                        value={formData.nameSo}
                                        onChange={e => setFormData(prev => ({ ...prev, nameSo: e.target.value }))}
                                        style={{
                                            width: '100%',
                                            padding: '12px 16px',
                                            borderRadius: 12,
                                            background: '#ffffff',
                                            border: '1px solid #D1D5DB',
                                            color: '#111827',
                                            fontSize: 14,
                                            outline: 'none',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Specifications: Brand, Condition, Model & Master SKU */}
                    <div style={{
                        background: 'var(--seller-card, #ffffff)',
                        border: '1px solid var(--seller-border, #e5e7eb)',
                        borderRadius: 18,
                        padding: 24,
                        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.03)'
                    }}>
                        <h3 style={{ fontSize: 17, fontWeight: 800, color: 'var(--seller-text, #111827)', margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Tag size={18} color="#FE2C55" />
                            <span>{rtl ? 'المواصفات والبيانات المرجعية' : 'Specifications & Identifiers'}</span>
                        </h3>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
                            <div>
                                <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--seller-text, #111827)', marginBottom: 6 }}>
                                    {rtl ? 'الماركة / البراند' : 'Brand Name'}
                                </label>
                                <input
                                    type="text"
                                    placeholder="مثال: Rolex, Apple, Gucci"
                                    value={formData.brand}
                                    onChange={e => setFormData(prev => ({ ...prev, brand: e.target.value }))}
                                    style={{
                                        width: '100%',
                                        padding: '11px 14px',
                                        borderRadius: 10,
                                        background: '#ffffff',
                                        border: '1px solid #D1D5DB',
                                        color: '#111827',
                                        fontSize: 14,
                                        outline: 'none',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--seller-text, #111827)', marginBottom: 6 }}>
                                    {rtl ? 'سنة الموديل / الإصدار' : 'Model / Year'}
                                </label>
                                <input
                                    type="text"
                                    placeholder="مثال: 2026 Edition"
                                    value={formData.model}
                                    onChange={e => setFormData(prev => ({ ...prev, model: e.target.value }))}
                                    style={{
                                        width: '100%',
                                        padding: '11px 14px',
                                        borderRadius: 10,
                                        background: '#ffffff',
                                        border: '1px solid #D1D5DB',
                                        color: '#111827',
                                        fontSize: 14,
                                        outline: 'none',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--seller-text, #111827)', marginBottom: 6 }}>
                                    {rtl ? 'حالة المنتج' : 'Condition'}
                                </label>
                                <select
                                    value={formData.condition}
                                    onChange={e => setFormData(prev => ({ ...prev, condition: e.target.value }))}
                                    style={{
                                        width: '100%',
                                        padding: '11px 14px',
                                        borderRadius: 10,
                                        background: '#ffffff',
                                        border: '1px solid #D1D5DB',
                                        color: '#111827',
                                        fontSize: 14,
                                        outline: 'none',
                                        boxSizing: 'border-box'
                                    }}
                                >
                                    <option value="NEW">{rtl ? 'جديد كلياً بكرتونه الأصلي (Brand New)' : 'Brand New'}</option>
                                    <option value="USED">{rtl ? 'مستعمل بحالة ممتازة كالجديد (Like New)' : 'Like New'}</option>
                                    <option value="REFURBISHED">{rtl ? 'مجدد ومعتمد من الشركة (Refurbished)' : 'Refurbished'}</option>
                                </select>
                            </div>

                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                    <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--seller-text, #111827)' }}>
                                        {rtl ? 'رمز SKU المرجعي' : 'Master SKU'}
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, sku: 'LX-' + Math.floor(100000 + Math.random() * 900000) }))}
                                        style={{ background: 'none', border: 'none', color: '#FE2C55', fontSize: 11.5, cursor: 'pointer', fontWeight: 700 }}
                                    >
                                        ⚡ {rtl ? 'توليد تلقائي' : 'Generate'}
                                    </button>
                                </div>
                                <input
                                    type="text"
                                    value={formData.sku}
                                    onChange={e => setFormData(prev => ({ ...prev, sku: e.target.value }))}
                                    style={{
                                        width: '100%',
                                        padding: '11px 14px',
                                        borderRadius: 10,
                                        background: '#ffffff',
                                        border: '1px solid #D1D5DB',
                                        color: '#FE2C55',
                                        fontWeight: 800,
                                        fontSize: 14,
                                        outline: 'none',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ═════════════════════════════════════════════════════════════
                STAGE 3: LUXURY MEDIA STUDIO (استوديو الوسائط المتعددة)
               ═════════════════════════════════════════════════════════════ */}
            {currentStep === 3 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    
                    {/* Hidden Inputs for File Pickers and Direct Camera Capture */}
                    <input ref={coverInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleCoverUpload} />
                    <input ref={coverCameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleCoverUpload} />
                    
                    <input ref={galleryInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleGalleryUpload} />
                    <input ref={galleryCameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleGalleryUpload} />
                    
                    <input ref={videoInputRef} type="file" accept="video/*" multiple style={{ display: 'none' }} onChange={handleVideoUpload} />
                    <input ref={videoCameraInputRef} type="file" accept="video/*" capture="environment" style={{ display: 'none' }} onChange={handleVideoUpload} />
                    
                    <input ref={multiMediaInputRef} type="file" accept="image/*,video/*" multiple style={{ display: 'none' }} onChange={(e) => handleBatchMediaUpload(e.target.files)} />

                    {/* Studio Header & Quick Filter Pills */}
                    <div style={{
                        background: 'var(--seller-card, #ffffff)',
                        border: '1px solid var(--seller-border, #e5e7eb)',
                        borderRadius: 18,
                        padding: '16px 20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 12,
                        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.03)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{
                                width: 44,
                                height: 44,
                                borderRadius: 12,
                                background: '#FFF0F3',
                                border: '1px solid #FECDD3',
                                display: 'flex',
                                alignItems: 'center',
                                justifyCenter: 'center',
                                justifyContent: 'center'
                            }}>
                                <Film size={22} color="#FE2C55" />
                            </div>
                            <div>
                                <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--seller-text, #111827)', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span>{rtl ? 'استوديو الوسائط الفاخر' : 'Luxury Media Studio'}</span>
                                    <span style={{ fontSize: 11, background: '#FFF0F3', color: '#FE2C55', padding: '2px 8px', borderRadius: 999, fontWeight: 700 }}>
                                        {formData.images.length} {rtl ? 'صور' : 'Photos'} • {(formData.videos?.length || (formData.videoUrl ? 1 : 0))} {rtl ? 'فيديو' : 'Videos'}
                                    </span>
                                </h3>
                                <p style={{ fontSize: 12, color: 'var(--seller-text-muted, #6b7280)', margin: '2px 0 0 0' }}>
                                    {rtl ? 'ارفع الصور ومقاطع الفيديو بدقة فائقة لجذب عملاء الفخامة في الخليج' : 'Upload ultra-HD photos & reels to convert premium buyers'}
                                </p>
                            </div>
                        </div>

                        {/* Filter Tabs */}
                        <div style={{
                            display: 'flex',
                            gap: 6,
                            background: '#F3F4F6',
                            padding: 4,
                            borderRadius: 12,
                            border: '1px solid #E5E7EB'
                        }}>
                            {[
                                { key: 'ALL', label: rtl ? 'الكل' : 'All', count: formData.images.length + (formData.videos?.length || (formData.videoUrl ? 1 : 0)) },
                                { key: 'PHOTOS', label: rtl ? 'الصور' : 'Photos', count: formData.images.length },
                                { key: 'VIDEOS', label: rtl ? 'الفيديوهات' : 'Videos', count: (formData.videos?.length || (formData.videoUrl ? 1 : 0)) }
                            ].map(tab => (
                                <button
                                    key={tab.key}
                                    type="button"
                                    onClick={() => setMediaActiveTab(tab.key)}
                                    style={{
                                        background: mediaActiveTab === tab.key ? '#FE2C55' : 'transparent',
                                        color: mediaActiveTab === tab.key ? '#ffffff' : '#4B5563',
                                        border: 'none',
                                        borderRadius: 8,
                                        padding: '6px 12px',
                                        fontSize: 12,
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        transition: 'all 0.15s ease'
                                    }}
                                >
                                    <span>{tab.label}</span>
                                    <span style={{
                                        fontSize: 10,
                                        background: mediaActiveTab === tab.key ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.08)',
                                        padding: '1px 6px',
                                        borderRadius: 999
                                    }}>
                                        {tab.count}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Progress Banner during Upload */}
                    {(uploadingCover || uploadingGallery || uploadingVideo) && (
                        <div style={{
                            background: '#FFF0F3',
                            border: '1px solid #FECDD3',
                            borderRadius: 14,
                            padding: '14px 18px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                <span style={{ fontSize: 13, fontWeight: 700, color: '#FE2C55', display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <RefreshCw size={15} className="spin-slow" />
                                    <span>{uploadStatusText || (rtl ? 'جاري رفع الملفات ومعالجتها...' : 'Uploading & processing...')}</span>
                                </span>
                                <span style={{ fontSize: 13, fontWeight: 800, color: '#FE2C55' }}>{uploadProgress}%</span>
                            </div>
                            <div style={{ width: '100%', height: 6, background: '#FCE7F3', borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{
                                    height: '100%',
                                    width: `${Math.max(5, uploadProgress)}%`,
                                    background: 'linear-gradient(90deg, #FE2C55 0%, #E11D48 100%)',
                                    borderRadius: 3,
                                    transition: 'width 0.2s ease'
                                }} />
                            </div>
                        </div>
                    )}

                    {/* Omni Drag & Drop Hub (Universal Drop Zone) */}
                    <div
                        onDragOver={(e) => { e.preventDefault(); setIsDraggingMedia(true); }}
                        onDragLeave={() => setIsDraggingMedia(false)}
                        onDrop={(e) => {
                            e.preventDefault();
                            setIsDraggingMedia(false);
                            if (e.dataTransfer?.files?.length) {
                                handleBatchMediaUpload(e.dataTransfer.files);
                            }
                        }}
                        style={{
                            border: isDraggingMedia ? '2px dashed #FE2C55' : '2px dashed #D1D5DB',
                            borderRadius: 18,
                            padding: '28px 20px',
                            textAlign: 'center',
                            background: isDraggingMedia ? '#FFF0F3' : '#F9FAFB',
                            boxShadow: isDraggingMedia ? '0 0 20px rgba(254, 44, 85, 0.2)' : 'none',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <div style={{
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            background: '#FFF0F3',
                            border: '1px solid #FECDD3',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 12px auto'
                        }}>
                            <Upload size={30} color="#FE2C55" />
                        </div>

                        <h4 style={{ fontSize: 16, fontWeight: 800, color: 'var(--seller-text, #111827)', margin: '0 0 6px 0' }}>
                            {rtl ? 'اسحب وأفلت صور وفيديوهات المنتج دفعة واحدة هنا' : 'Drag & drop photos and videos together here'}
                        </h4>
                        <p style={{ fontSize: 12.5, color: 'var(--seller-text-muted, #6b7280)', margin: '0 0 18px 0' }}>
                            {rtl ? 'يمكنك تحديد مجموعة صور ومقاطع فيديو معاً وسيتم تصنيفها وتجهيزها تلقائياً' : 'Select multiple images and video clips simultaneously'}
                        </p>

                        {/* Direct Mobile & Desktop Action Triggers */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 10 }}>
                            <button
                                type="button"
                                onClick={() => multiMediaInputRef.current?.click()}
                                style={{
                                    background: '#FE2C55',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: 12,
                                    padding: '10px 18px',
                                    fontSize: 13,
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    boxShadow: '0 4px 14px rgba(254, 44, 85, 0.35)'
                                }}
                            >
                                <Upload size={16} />
                                <span>{rtl ? 'اختيار ملفات من الجهاز' : 'Browse Files'}</span>
                            </button>

                            <button
                                type="button"
                                onClick={() => galleryCameraInputRef.current?.click()}
                                style={{
                                    background: '#F3F4F6',
                                    color: '#111827',
                                    border: '1px solid #E5E7EB',
                                    borderRadius: 12,
                                    padding: '10px 18px',
                                    fontSize: 13,
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8
                                }}
                            >
                                <Camera size={16} />
                                <span>{rtl ? 'التقاط بالكاميرا' : 'Take Photo'}</span>
                            </button>

                            <button
                                type="button"
                                onClick={() => videoCameraInputRef.current?.click()}
                                style={{
                                    background: '#F3F4F6',
                                    color: '#111827',
                                    border: '1px solid #E5E7EB',
                                    borderRadius: 12,
                                    padding: '10px 18px',
                                    fontSize: 13,
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8
                                }}
                            >
                                <Video size={16} />
                                <span>{rtl ? 'تسجيل ريل فيديو' : 'Record Video'}</span>
                            </button>
                        </div>
                    </div>

                    {/* ── SECTION 1: PRIMARY COVER PHOTO ── */}
                    {(mediaActiveTab === 'ALL' || mediaActiveTab === 'PHOTOS') && (
                        <div style={{
                            background: 'var(--seller-card, #ffffff)',
                            border: '1px solid var(--seller-border, #e5e7eb)',
                            borderRadius: 18,
                            padding: 20,
                            boxShadow: '0 1px 4px rgba(0, 0, 0, 0.03)'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                                <div>
                                    <h4 style={{ fontSize: 15, fontWeight: 700, color: 'var(--seller-text, #111827)', margin: '0 0 2px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <Star size={16} color="#eab308" fill="#eab308" />
                                        <span>{rtl ? 'صورة الغلاف الرسمية للمنتج *' : 'Primary Cover Photo *'}</span>
                                    </h4>
                                    <p style={{ fontSize: 12, color: 'var(--seller-text-muted, #6b7280)', margin: 0 }}>
                                        {rtl ? 'الصورة المميزة التي ستتصدر صفحات المتجر ونتائج البحث' : 'Main featured hero photo for search and store listings'}
                                    </p>
                                </div>

                                {formData.coverImageUrl && (
                                    <span style={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        color: '#b45309',
                                        background: 'rgba(234, 179, 8, 0.15)',
                                        border: '1px solid rgba(234, 179, 8, 0.3)',
                                        borderRadius: 8,
                                        padding: '4px 10px'
                                    }}>
                                        ★ {rtl ? 'الغلاف جاهز' : 'Cover Set'}
                                    </span>
                                )}
                            </div>

                            {formData.coverImageUrl ? (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 12
                                }}>
                                    <div style={{
                                        position: 'relative',
                                        width: '100%',
                                        maxWidth: 420,
                                        aspectRatio: '16/10',
                                        borderRadius: 16,
                                        overflow: 'hidden',
                                        border: '2px solid #eab308',
                                        boxShadow: '0 8px 24px rgba(234, 179, 8, 0.15)'
                                    }}>
                                        <img 
                                            src={resolveStorefrontImageUrl(formData.coverImageUrl)} 
                                            alt="Main Cover" 
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                                        />
                                        <div style={{
                                            position: 'absolute',
                                            top: 10,
                                            right: rtl ? 10 : 'auto',
                                            left: rtl ? 'auto' : 10,
                                            background: 'rgba(234, 179, 8, 0.95)',
                                            color: '#000000',
                                            fontWeight: 800,
                                            fontSize: 11,
                                            padding: '4px 10px',
                                            borderRadius: 8,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                                        }}>
                                            ★ {rtl ? 'الغلاف المعتمد' : 'Official Cover'}
                                        </div>
                                    </div>

                                    {/* Cover Image Action Buttons */}
                                    <div style={{ display: 'flex', gap: 8, width: '100%', maxWidth: 420 }}>
                                        <button
                                            type="button"
                                            onClick={() => coverInputRef.current?.click()}
                                            style={{
                                                flex: 1,
                                                padding: '8px 14px',
                                                borderRadius: 10,
                                                background: '#F9FAFB',
                                                color: '#111827',
                                                border: '1px solid #D1D5DB',
                                                fontSize: 12.5,
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 6
                                            }}
                                        >
                                            <Upload size={14} />
                                            <span>{rtl ? 'تغيير الغلاف' : 'Replace Cover'}</span>
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => coverCameraInputRef.current?.click()}
                                            style={{
                                                padding: '8px 14px',
                                                borderRadius: 10,
                                                background: '#F9FAFB',
                                                color: '#111827',
                                                border: '1px solid #D1D5DB',
                                                fontSize: 12.5,
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 6
                                            }}
                                        >
                                            <Camera size={14} />
                                            <span>{rtl ? 'كاميرا' : 'Camera'}</span>
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => {
                                                setFormData(prev => {
                                                    const remaining = prev.images.filter(u => u !== prev.coverImageUrl);
                                                    return { ...prev, coverImageUrl: remaining[0] || '', images: remaining };
                                                });
                                            }}
                                            style={{
                                                padding: '8px 14px',
                                                borderRadius: 10,
                                                background: '#FFF0F3',
                                                color: '#FE2C55',
                                                border: '1px solid #FECDD3',
                                                fontSize: 12.5,
                                                fontWeight: 600,
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <Trash2 size={15} />
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 10,
                                    padding: '24px 16px',
                                    borderRadius: 14,
                                    border: '1px dashed rgba(234, 179, 8, 0.4)',
                                    background: 'rgba(234, 179, 8, 0.04)',
                                    textAlign: 'center'
                                }}>
                                    <div style={{
                                        width: 50,
                                        height: 50,
                                        borderRadius: '50%',
                                        background: 'rgba(234, 179, 8, 0.15)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto'
                                    }}>
                                        <Star size={24} color="#eab308" />
                                    </div>
                                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--seller-text, #111827)' }}>
                                        {rtl ? 'لم يتم تحديد صورة غلاف رئيسية بعد' : 'No primary cover photo selected yet'}
                                    </div>
                                    <div style={{ fontSize: 12, color: 'var(--seller-text-muted, #6b7280)' }}>
                                        {rtl ? 'اختر صورة من المعرض بالأسفل أو التقط صورة غلاف جديدة' : 'Pick a photo from your gallery below or upload directly'}
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 4 }}>
                                        <button
                                            type="button"
                                            onClick={() => coverInputRef.current?.click()}
                                            style={{
                                                background: '#FE2C55',
                                                color: '#ffffff',
                                                border: 'none',
                                                borderRadius: 10,
                                                padding: '8px 16px',
                                                fontSize: 12.5,
                                                fontWeight: 700,
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {rtl ? 'رفع صورة الغلاف +' : 'Upload Cover +'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => coverCameraInputRef.current?.click()}
                                            style={{
                                                background: '#F3F4F6',
                                                color: '#111827',
                                                border: '1px solid #E5E7EB',
                                                borderRadius: 10,
                                                padding: '8px 16px',
                                                fontSize: 12.5,
                                                fontWeight: 700,
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {rtl ? 'التقاط بالكاميرا 📸' : 'Camera 📸'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── SECTION 2: GALLERY PHOTOS GRID ── */}
                    {(mediaActiveTab === 'ALL' || mediaActiveTab === 'PHOTOS') && (
                        <div style={{
                            background: 'var(--seller-card, #ffffff)',
                            border: '1px solid var(--seller-border, #e5e7eb)',
                            borderRadius: 18,
                            padding: 20,
                            boxShadow: '0 1px 4px rgba(0, 0, 0, 0.03)'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
                                <div>
                                    <h4 style={{ fontSize: 15, fontWeight: 700, color: 'var(--seller-text, #111827)', margin: '0 0 2px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <ImageIcon size={16} color="#FE2C55" />
                                        <span>{rtl ? 'معرض صور المنتج (Gallery Photos)' : 'Product Gallery Photos'}</span>
                                        <span style={{ fontSize: 11, background: '#FFF0F3', color: '#FE2C55', padding: '2px 8px', borderRadius: 999 }}>
                                            {formData.images.length}
                                        </span>
                                    </h4>
                                    <p style={{ fontSize: 12, color: 'var(--seller-text-muted, #6b7280)', margin: 0 }}>
                                        {rtl ? 'انقر على النجمة ★ لجعل أي صورة هي الغلاف، أو استخدم الأسهم لإعادة ترتيبها' : 'Click ★ to make any photo cover, or arrows to reorder'}
                                    </p>
                                </div>

                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button
                                        type="button"
                                        onClick={() => galleryCameraInputRef.current?.click()}
                                        style={{
                                            background: '#F9FAFB',
                                            color: '#111827',
                                            border: '1px solid #E5E7EB',
                                            borderRadius: 10,
                                            padding: '7px 12px',
                                            fontSize: 12,
                                            fontWeight: 700,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6
                                        }}
                                    >
                                        <Camera size={14} />
                                        <span>{rtl ? 'كاميرا' : 'Camera'}</span>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => galleryInputRef.current?.click()}
                                        style={{
                                            background: '#FFF0F3',
                                            color: '#FE2C55',
                                            border: '1px solid #FECDD3',
                                            borderRadius: 10,
                                            padding: '7px 14px',
                                            fontSize: 12,
                                            fontWeight: 700,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6
                                        }}
                                    >
                                        <Plus size={15} />
                                        <span>{rtl ? 'إضافة صور +' : 'Add Photos +'}</span>
                                    </button>
                                </div>
                            </div>

                            {formData.images.length > 0 ? (
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
                                    gap: 12
                                }}>
                                    {formData.images.map((imgUrl, idx) => {
                                        const isCover = formData.coverImageUrl === imgUrl;
                                        return (
                                            <div
                                                key={idx}
                                                style={{
                                                    position: 'relative',
                                                    borderRadius: 14,
                                                    overflow: 'hidden',
                                                    aspectRatio: '1',
                                                    background: '#F3F4F6',
                                                    border: isCover ? '2px solid #eab308' : '1px solid #E5E7EB',
                                                    boxShadow: isCover ? '0 4px 16px rgba(234, 179, 8, 0.25)' : 'none',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <img 
                                                    src={resolveStorefrontImageUrl(imgUrl)} 
                                                    alt={`Photo ${idx + 1}`} 
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                                                />

                                                {/* Cover Badge */}
                                                {isCover && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: 6,
                                                        right: rtl ? 6 : 'auto',
                                                        left: rtl ? 'auto' : 6,
                                                        background: '#eab308',
                                                        color: '#000000',
                                                        fontSize: 9.5,
                                                        fontWeight: 900,
                                                        padding: '2px 6px',
                                                        borderRadius: 6
                                                    }}>
                                                        ★ {rtl ? 'غلاف' : 'Cover'}
                                                    </div>
                                                )}

                                                {/* Image Sequence Number */}
                                                <div style={{
                                                    position: 'absolute',
                                                    top: 6,
                                                    left: rtl ? 6 : 'auto',
                                                    right: rtl ? 'auto' : 6,
                                                    background: 'rgba(0, 0, 0, 0.65)',
                                                    color: '#ffffff',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 6
                                                }}>
                                                    #{idx + 1}
                                                </div>

                                                {/* Overlay Control Bar */}
                                                <div style={{
                                                    position: 'absolute',
                                                    bottom: 0,
                                                    left: 0,
                                                    right: 0,
                                                    background: 'linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.4) 80%, transparent 100%)',
                                                    padding: '6px 4px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    gap: 2
                                                }}>
                                                    {/* Move Backward */}
                                                    <button
                                                        type="button"
                                                        disabled={idx === 0}
                                                        onClick={() => handleMoveImage(idx, idx - 1)}
                                                        title={rtl ? 'تحريك للخلف' : 'Move back'}
                                                        style={{
                                                            background: 'rgba(255, 255, 255, 0.2)',
                                                            color: idx === 0 ? '#9CA3AF' : '#ffffff',
                                                            border: 'none',
                                                            borderRadius: 6,
                                                            width: 24,
                                                            height: 24,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            cursor: idx === 0 ? 'not-allowed' : 'pointer'
                                                        }}
                                                    >
                                                        {rtl ? '→' : '←'}
                                                    </button>

                                                    {/* Make Cover Button */}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSetAsCover(imgUrl)}
                                                        title={rtl ? 'تعيين كصورة غلاف' : 'Set as cover'}
                                                        style={{
                                                            background: isCover ? 'rgba(234, 179, 8, 0.5)' : 'rgba(255, 255, 255, 0.25)',
                                                            color: isCover ? '#fef08a' : '#ffffff',
                                                            border: 'none',
                                                            borderRadius: 6,
                                                            padding: '2px 6px',
                                                            fontSize: 11,
                                                            fontWeight: 700,
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 2
                                                        }}
                                                    >
                                                        ★
                                                    </button>

                                                    {/* Move Forward */}
                                                    <button
                                                        type="button"
                                                        disabled={idx === formData.images.length - 1}
                                                        onClick={() => handleMoveImage(idx, idx + 1)}
                                                        title={rtl ? 'تحريك للأمام' : 'Move forward'}
                                                        style={{
                                                            background: 'rgba(255, 255, 255, 0.2)',
                                                            color: idx === formData.images.length - 1 ? '#9CA3AF' : '#ffffff',
                                                            border: 'none',
                                                            borderRadius: 6,
                                                            width: 24,
                                                            height: 24,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            cursor: idx === formData.images.length - 1 ? 'not-allowed' : 'pointer'
                                                        }}
                                                    >
                                                        {rtl ? '←' : '→'}
                                                    </button>

                                                    {/* Delete Button */}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveImage(idx)}
                                                        title={rtl ? 'حذف الصورة' : 'Delete photo'}
                                                        style={{
                                                            background: 'rgba(239, 68, 68, 0.5)',
                                                            color: '#ffffff',
                                                            border: 'none',
                                                            borderRadius: 6,
                                                            width: 24,
                                                            height: 24,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        <Trash2 size={12} />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div style={{
                                    padding: '24px 16px',
                                    textAlign: 'center',
                                    background: '#F9FAFB',
                                    borderRadius: 14,
                                    border: '1px dashed #E5E7EB',
                                    color: '#6B7280',
                                    fontSize: 13
                                }}>
                                    {rtl ? 'لم يتم إضافة صور للمعرض بعد. اسحب صوراً إلى منطقة الرفع بالأعلى.' : 'No gallery photos added yet. Drag or pick photos above.'}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── SECTION 3: PRODUCT VIDEOS & REELS ── */}
                    {(mediaActiveTab === 'ALL' || mediaActiveTab === 'VIDEOS') && (
                        <div style={{
                            background: 'var(--seller-card, #ffffff)',
                            border: '1px solid var(--seller-border, #e5e7eb)',
                            borderRadius: 18,
                            padding: 20,
                            boxShadow: '0 1px 4px rgba(0, 0, 0, 0.03)'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
                                <div>
                                    <h4 style={{ fontSize: 15, fontWeight: 700, color: 'var(--seller-text, #111827)', margin: '0 0 2px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <Video size={16} color="#FE2C55" />
                                        <span>{rtl ? 'مقاطع الفيديو والريلز (Reels & Videos)' : 'Product Videos & Reels'}</span>
                                        <span style={{ fontSize: 11, background: '#FFF0F3', color: '#FE2C55', padding: '2px 8px', borderRadius: 999 }}>
                                            {(formData.videos?.length || (formData.videoUrl ? 1 : 0))}
                                        </span>
                                    </h4>
                                    <p style={{ fontSize: 12, color: 'var(--seller-text-muted, #6b7280)', margin: 0 }}>
                                        {rtl ? 'مقاطع الفيديو ترفع ثقة المتسوقين وتضاعف معدل إتمام الشراء' : 'Video clips boost customer trust and conversion rates up to 300%'}
                                    </p>
                                </div>

                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button
                                        type="button"
                                        onClick={() => videoCameraInputRef.current?.click()}
                                        style={{
                                            background: '#F9FAFB',
                                            color: '#111827',
                                            border: '1px solid #E5E7EB',
                                            borderRadius: 10,
                                            padding: '7px 12px',
                                            fontSize: 12,
                                            fontWeight: 700,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6
                                        }}
                                    >
                                        <Video size={14} />
                                        <span>{rtl ? 'تسجيل بالكاميرا' : 'Record Video'}</span>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => videoInputRef.current?.click()}
                                        style={{
                                            background: '#FFF0F3',
                                            color: '#FE2C55',
                                            border: '1px solid #FECDD3',
                                            borderRadius: 10,
                                            padding: '7px 14px',
                                            fontSize: 12,
                                            fontWeight: 700,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6
                                        }}
                                    >
                                        <Plus size={15} />
                                        <span>{rtl ? 'رفع فيديو +' : 'Add Video +'}</span>
                                    </button>
                                </div>
                            </div>

                            {/* Render Video Cards */}
                            {((formData.videos && formData.videos.length > 0) || formData.videoUrl) ? (
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                    gap: 16
                                }}>
                                    {(formData.videos && formData.videos.length > 0 ? formData.videos : [formData.videoUrl]).filter(Boolean).map((vUrl, vIdx) => {
                                        const isPrimaryVideo = formData.videoUrl === vUrl;
                                        return (
                                            <div
                                                key={vIdx}
                                                style={{
                                                    background: '#000000',
                                                    borderRadius: 14,
                                                    overflow: 'hidden',
                                                    border: isPrimaryVideo ? '2px solid #FE2C55' : '1px solid #E5E7EB',
                                                    boxShadow: isPrimaryVideo ? '0 4px 20px rgba(254, 44, 85, 0.25)' : 'none'
                                                }}
                                            >
                                                <div style={{ position: 'relative', width: '100%', height: 180, background: '#000000' }}>
                                                    <video
                                                        src={resolveStorefrontImageUrl(vUrl)}
                                                        controls
                                                        preload="metadata"
                                                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                                    />
                                                    {isPrimaryVideo && (
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: 8,
                                                            right: rtl ? 8 : 'auto',
                                                            left: rtl ? 'auto' : 8,
                                                            background: '#FE2C55',
                                                            color: '#ffffff',
                                                            fontSize: 10,
                                                            fontWeight: 800,
                                                            padding: '3px 8px',
                                                            borderRadius: 6
                                                        }}>
                                                            ★ {rtl ? 'الفيديو الرئيسي' : 'Primary Reel'}
                                                        </div>
                                                    )}
                                                </div>

                                                <div style={{
                                                    padding: '10px 14px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    background: '#ffffff',
                                                    borderTop: '1px solid #E5E7EB'
                                                }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSetAsPrimaryVideo(vUrl)}
                                                        style={{
                                                            background: isPrimaryVideo ? '#FFF0F3' : '#F9FAFB',
                                                            color: isPrimaryVideo ? '#FE2C55' : '#4B5563',
                                                            border: isPrimaryVideo ? '1px solid #FECDD3' : '1px solid #E5E7EB',
                                                            borderRadius: 8,
                                                            padding: '5px 10px',
                                                            fontSize: 11.5,
                                                            fontWeight: 700,
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        {isPrimaryVideo ? (rtl ? '✓ الفيديو الرئيسي' : '✓ Primary') : (rtl ? 'تعيين كرئيسي' : 'Make Primary')}
                                                    </button>

                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveVideo(vIdx)}
                                                        style={{
                                                            background: '#FFF0F3',
                                                            color: '#FE2C55',
                                                            border: 'none',
                                                            borderRadius: 8,
                                                            padding: '5px 10px',
                                                            fontSize: 11.5,
                                                            fontWeight: 700,
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 4
                                                        }}
                                                    >
                                                        <Trash2 size={13} />
                                                        <span>{rtl ? 'حذف' : 'Delete'}</span>
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div style={{
                                    padding: '24px 16px',
                                    textAlign: 'center',
                                    background: '#F9FAFB',
                                    borderRadius: 14,
                                    border: '1px dashed #E5E7EB',
                                    color: '#6B7280',
                                    fontSize: 13
                                }}>
                                    {rtl ? 'لم يتم إضافة أي فيديو بعد. انقر على "رفع فيديو +" أو اسحب ملف فيديو إلى هنا.' : 'No videos added yet. Click "Add Video +" or drag video files.'}
                                </div>
                            )}

                            {/* External Video URL Link Input */}
                            <div style={{
                                marginTop: 14,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                background: '#F9FAFB',
                                padding: '10px 14px',
                                borderRadius: 12,
                                border: '1px solid #E5E7EB'
                            }}>
                                <span style={{ fontSize: 12, color: 'var(--seller-text-muted, #6b7280)', whiteSpace: 'nowrap' }}>
                                    {rtl ? 'أو رابط فيديو خارجي (CDN/MP4):' : 'Or external video link:'}
                                </span>
                                <input
                                    type="text"
                                    placeholder="https://..."
                                    value={formData.videoUrl}
                                    onChange={e => {
                                        const url = e.target.value;
                                        setFormData(prev => ({
                                            ...prev,
                                            videoUrl: url,
                                            videos: url ? [...new Set([...(prev.videos || []), url])] : prev.videos
                                        }));
                                    }}
                                    style={{
                                        flex: 1,
                                        padding: '7px 12px',
                                        borderRadius: 8,
                                        background: '#ffffff',
                                        border: '1px solid #D1D5DB',
                                        color: '#111827',
                                        fontSize: 12.5,
                                        outline: 'none'
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ═════════════════════════════════════════════════════════════
                STAGE 4: COLORS, SIZES & VARIANTS MATRIX (الخيارات والمصفوفة)
               ═════════════════════════════════════════════════════════════ */}
            {currentStep === 4 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                    
                    {/* Color Swatches */}
                    <div style={{
                        background: 'var(--seller-card, #ffffff)',
                        border: '1px solid var(--seller-border, #e5e7eb)',
                        borderRadius: 18,
                        padding: 24,
                        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.03)'
                    }}>
                        <h3 style={{ fontSize: 17, fontWeight: 800, color: 'var(--seller-text, #111827)', margin: '0 0 6px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Sliders size={18} color="#FE2C55" />
                            <span>{rtl ? 'ألوان وخيارات المنتج (Color Swatches)' : 'Product Colors'}</span>
                        </h3>
                        <p style={{ fontSize: 13, color: 'var(--seller-text-muted, #6b7280)', margin: '0 0 16px 0' }}>
                            {rtl ? 'اختر من الألوان الملكية الجاهزة أو أضف لوناً مخصصاً لمنتجك.' : 'Choose preset colors or add custom swatches.'}
                        </p>

                        {/* Presets Grid */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 18 }}>
                            {PRESET_COLORS.map(c => {
                                const isAdded = formData.selectedColors.some(item => item.hex.toLowerCase() === c.hex.toLowerCase());
                                return (
                                    <button
                                        type="button"
                                        key={c.hex}
                                        onClick={() => {
                                            setFormData(prev => ({
                                                ...prev,
                                                selectedColors: isAdded
                                                    ? prev.selectedColors.filter(item => item.hex.toLowerCase() !== c.hex.toLowerCase())
                                                    : [...prev.selectedColors, { name: c.name, nameAr: c.nameAr, hex: c.hex, imageUrl: '' }]
                                            }));
                                        }}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            padding: '7px 12px',
                                            borderRadius: 10,
                                            background: isAdded ? '#FFF0F3' : '#F9FAFB',
                                            border: isAdded ? '1px solid #FE2C55' : '1px solid #E5E7EB',
                                            color: isAdded ? '#FE2C55' : '#374151',
                                            fontSize: 12.5,
                                            fontWeight: isAdded ? 700 : 600,
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <span style={{ width: 14, height: 14, borderRadius: '50%', background: c.hex, border: '1px solid rgba(0, 0, 0, 0.15)' }} />
                                        <span>{rtl ? c.nameAr : c.name}</span>
                                        {isAdded && <Check size={14} strokeWidth={2.5} />}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Custom Color Input */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#F9FAFB', padding: '10px 14px', borderRadius: 12, border: '1px solid #E5E7EB' }}>
                            <input 
                                type="color" 
                                value={formData.customColorHex} 
                                onChange={e => setFormData(prev => ({ ...prev, customColorHex: e.target.value }))}
                                style={{ width: 36, height: 36, border: 'none', borderRadius: 8, background: 'none', cursor: 'pointer' }}
                            />
                            <input
                                type="text"
                                placeholder={rtl ? 'اسم اللون المخصص (مثال: برونزي فاخر)' : 'Custom color name'}
                                value={formData.customColorName}
                                onChange={e => setFormData(prev => ({ ...prev, customColorName: e.target.value }))}
                                style={{
                                    flex: 1,
                                    padding: '8px 12px',
                                    borderRadius: 8,
                                    background: '#ffffff',
                                    border: '1px solid #D1D5DB',
                                    color: '#111827',
                                    fontSize: 13,
                                    outline: 'none'
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    if (formData.customColorName.trim()) {
                                        setFormData(prev => ({
                                            ...prev,
                                            selectedColors: [...prev.selectedColors, {
                                                name: prev.customColorName,
                                                nameAr: prev.customColorName,
                                                hex: prev.customColorHex,
                                                imageUrl: ''
                                            }],
                                            customColorName: ''
                                        }));
                                    }
                                }}
                                style={{
                                    background: '#FE2C55',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: 8,
                                    padding: '8px 16px',
                                    fontSize: 13,
                                    fontWeight: 700,
                                    cursor: 'pointer'
                                }}
                            >
                                {rtl ? 'إضافة لون +' : 'Add Color +'}
                            </button>
                        </div>
                    </div>

                    {/* Sizes Selection */}
                    <div style={{
                        background: 'var(--seller-card, #ffffff)',
                        border: '1px solid var(--seller-border, #e5e7eb)',
                        borderRadius: 18,
                        padding: 24,
                        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.03)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
                            <h3 style={{ fontSize: 17, fontWeight: 800, color: 'var(--seller-text, #111827)', margin: 0 }}>
                                {rtl ? 'المقاسات والسعات (Sizes & Capacities)' : 'Product Sizes'}
                            </h3>

                            {/* Preset Category Switcher */}
                            <div style={{ display: 'flex', gap: 6 }}>
                                {Object.entries(SIZE_PRESETS).map(([key, item]) => (
                                    <button
                                        type="button"
                                        key={key}
                                        onClick={() => setFormData(prev => ({ ...prev, sizeType: key, selectedSizes: item.sizes.slice(0, 4) }))}
                                        style={{
                                            background: formData.sizeType === key ? '#FFF0F3' : '#F9FAFB',
                                            color: formData.sizeType === key ? '#FE2C55' : '#6B7280',
                                            border: formData.sizeType === key ? '1px solid #FE2C55' : '1px solid #E5E7EB',
                                            borderRadius: 8,
                                            padding: '5px 12px',
                                            fontSize: 12,
                                            fontWeight: formData.sizeType === key ? 700 : 600,
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {item.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Preset Sizes Checkbox Chips */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                            {SIZE_PRESETS[formData.sizeType]?.sizes.map(sz => {
                                const isSelected = formData.selectedSizes.includes(sz);
                                return (
                                    <button
                                        type="button"
                                        key={sz}
                                        onClick={() => {
                                            setFormData(prev => ({
                                                ...prev,
                                                selectedSizes: isSelected
                                                    ? prev.selectedSizes.filter(s => s !== sz)
                                                    : [...prev.selectedSizes, sz]
                                            }));
                                        }}
                                        style={{
                                            padding: '7px 16px',
                                            borderRadius: 8,
                                            background: isSelected ? '#FFF0F3' : '#F9FAFB',
                                            border: isSelected ? '1px solid #FE2C55' : '1px solid #E5E7EB',
                                            color: isSelected ? '#FE2C55' : '#374151',
                                            fontSize: 13,
                                            fontWeight: 700,
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {sz}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Auto Generate Variants Button & Matrix */}
                    <div style={{
                        background: 'var(--seller-card, #ffffff)',
                        border: '1px solid var(--seller-border, #e5e7eb)',
                        borderRadius: 18,
                        padding: 24,
                        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.03)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
                            <div>
                                <h3 style={{ fontSize: 17, fontWeight: 800, color: 'var(--seller-text, #111827)', margin: '0 0 4px 0' }}>
                                    {rtl ? 'مصفوفة الخيارات والمخزون (Variant Matrix)' : 'Generated Variant Matrix'}
                                </h3>
                                <p style={{ fontSize: 13, color: 'var(--seller-text-muted, #6b7280)', margin: 0 }}>
                                    {rtl ? `إجمالي الخيارات المولدة: ${formData.variants.length} خيار` : `Total Variants: ${formData.variants.length}`}
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={generateVariantMatrix}
                                style={{
                                    background: '#FE2C55',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: 10,
                                    padding: '9px 18px',
                                    fontSize: 13,
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    boxShadow: '0 4px 14px rgba(254, 44, 85, 0.35)'
                                }}
                            >
                                <Sparkles size={16} />
                                <span>{rtl ? 'توليد مصفوفة الخيارات تلقائياً ⚡' : 'Re-generate Matrix ⚡'}</span>
                            </button>
                        </div>

                        {/* Variants Table & Mobile Cards */}
                        {formData.variants.length > 0 && (
                            <>
                                {/* Desktop & Tablet Table View */}
                                <div className="variant-matrix-table-container" style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, textAlign: rtl ? 'right' : 'left' }}>
                                        <thead>
                                            <tr style={{ borderBottom: '1px solid #E5E7EB', color: '#6B7280', background: '#F9FAFB' }}>
                                                <th style={{ padding: '12px 10px' }}>{rtl ? 'اللون' : 'Color'}</th>
                                                <th style={{ padding: '12px 10px' }}>{rtl ? 'المقاس' : 'Size'}</th>
                                                <th style={{ padding: '12px 10px' }}>SKU</th>
                                                <th style={{ padding: '12px 10px' }}>{rtl ? 'السعر' : 'Price'}</th>
                                                <th style={{ padding: '12px 10px' }}>{rtl ? 'المخزون' : 'Stock'}</th>
                                                <th style={{ padding: '12px 10px' }}>{rtl ? 'متاح' : 'Active'}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {formData.variants.map((v, i) => (
                                                <tr key={i} style={{ borderBottom: '1px solid #F3F4F6' }}>
                                                    <td style={{ padding: '10px 8px' }}>
                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                            <span style={{ width: 12, height: 12, borderRadius: '50%', background: v.colorHex, border: '1px solid rgba(0, 0, 0, 0.15)' }} />
                                                            <span style={{ color: '#111827', fontWeight: 600 }}>{v.colorName}</span>
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '10px 8px', color: '#111827', fontWeight: 700 }}>{v.sizeName}</td>
                                                    <td style={{ padding: '10px 8px' }}>
                                                        <input
                                                            type="text"
                                                            value={v.sku}
                                                            onChange={e => {
                                                                const val = e.target.value;
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    variants: prev.variants.map((item, idx) => idx === i ? { ...item, sku: val } : item)
                                                                }));
                                                            }}
                                                            style={{ width: 130, padding: '6px 10px', borderRadius: 6, background: '#ffffff', border: '1px solid #D1D5DB', color: '#111827', fontSize: 12 }}
                                                        />
                                                    </td>
                                                    <td style={{ padding: '10px 8px' }}>
                                                        <input
                                                            type="number"
                                                            value={v.price}
                                                            onChange={e => {
                                                                const val = e.target.value;
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    variants: prev.variants.map((item, idx) => idx === i ? { ...item, price: val } : item)
                                                                }));
                                                            }}
                                                            style={{ width: 85, padding: '6px 10px', borderRadius: 6, background: '#ffffff', border: '1px solid #D1D5DB', color: '#111827', fontSize: 12, fontWeight: 700 }}
                                                        />
                                                    </td>
                                                    <td style={{ padding: '10px 8px' }}>
                                                        <input
                                                            type="number"
                                                            value={v.stock}
                                                            onChange={e => {
                                                                const val = e.target.value;
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    variants: prev.variants.map((item, idx) => idx === i ? { ...item, stock: parseInt(val) || 0 } : item)
                                                                }));
                                                            }}
                                                            style={{ width: 70, padding: '6px 10px', borderRadius: 6, background: '#ffffff', border: '1px solid #D1D5DB', color: '#10B981', fontWeight: 700, fontSize: 12 }}
                                                        />
                                                    </td>
                                                    <td style={{ padding: '10px 8px' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={v.isAvailable}
                                                            onChange={e => {
                                                                const checked = e.target.checked;
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    variants: prev.variants.map((item, idx) => idx === i ? { ...item, isAvailable: checked } : item)
                                                                }));
                                                            }}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Mobile Native Cards View */}
                                <div className="variant-matrix-cards-container">
                                    {formData.variants.map((v, i) => (
                                        <div
                                            key={i}
                                            style={{
                                                background: '#F9FAFB',
                                                border: '1px solid #E5E7EB',
                                                borderRadius: 14,
                                                padding: 14,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 10
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <span style={{ width: 16, height: 16, borderRadius: '50%', background: v.colorHex, border: '1px solid rgba(0, 0, 0, 0.15)' }} />
                                                    <span style={{ color: '#111827', fontWeight: 700, fontSize: 14 }}>{v.colorName}</span>
                                                    <span style={{ background: '#FFF0F3', color: '#FE2C55', padding: '2px 8px', borderRadius: 6, fontSize: 12, fontWeight: 800 }}>
                                                        {v.sizeName}
                                                    </span>
                                                </div>

                                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: v.isAvailable ? '#10B981' : '#6B7280', cursor: 'pointer' }}>
                                                    <span>{v.isAvailable ? (rtl ? 'متاح للطلب' : 'Available') : (rtl ? 'غير متاح' : 'Inactive')}</span>
                                                    <input
                                                        type="checkbox"
                                                        checked={v.isAvailable}
                                                        onChange={e => {
                                                            const checked = e.target.checked;
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                variants: prev.variants.map((item, idx) => idx === i ? { ...item, isAvailable: checked } : item)
                                                            }));
                                                        }}
                                                        style={{ width: 18, height: 18, cursor: 'pointer' }}
                                                    />
                                                </label>
                                            </div>

                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                                <div>
                                                    <label style={{ display: 'block', fontSize: 11, color: '#6B7280', marginBottom: 4 }}>
                                                        {rtl ? 'السعر (ر.س)' : 'Price'}
                                                    </label>
                                                    <input
                                                        type="number"
                                                        value={v.price}
                                                        onChange={e => {
                                                            const val = e.target.value;
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                variants: prev.variants.map((item, idx) => idx === i ? { ...item, price: val } : item)
                                                            }));
                                                        }}
                                                        style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8, background: '#ffffff', border: '1px solid #D1D5DB', color: '#111827', fontWeight: 700, fontSize: 13 }}
                                                    />
                                                </div>

                                                <div>
                                                    <label style={{ display: 'block', fontSize: 11, color: '#6B7280', marginBottom: 4 }}>
                                                        {rtl ? 'المخزون (الكمية)' : 'Stock'}
                                                    </label>
                                                    <input
                                                        type="number"
                                                        value={v.stock}
                                                        onChange={e => {
                                                            const val = e.target.value;
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                variants: prev.variants.map((item, idx) => idx === i ? { ...item, stock: parseInt(val) || 0 } : item)
                                                            }));
                                                        }}
                                                        style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8, background: '#ffffff', border: '1px solid #D1D5DB', color: '#10B981', fontWeight: 700, fontSize: 13 }}
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <label style={{ display: 'block', fontSize: 11, color: '#6B7280', marginBottom: 4 }}>
                                                    {rtl ? 'رمز SKU المرجعي' : 'SKU'}
                                                </label>
                                                <input
                                                    type="text"
                                                    value={v.sku}
                                                    onChange={e => {
                                                        const val = e.target.value;
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            variants: prev.variants.map((item, idx) => idx === i ? { ...item, sku: val } : item)
                                                        }));
                                                    }}
                                                    style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', borderRadius: 8, background: '#ffffff', border: '1px solid #D1D5DB', color: '#111827', fontSize: 12 }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* ═════════════════════════════════════════════════════════════
                STAGE 5: PRICING, INVENTORY & LOGISTICS (الأسعار والمخزون)
               ═════════════════════════════════════════════════════════════ */}
            {currentStep === 5 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                    
                    {/* Retail Pricing */}
                    <div style={{
                        background: 'var(--seller-card, #ffffff)',
                        border: '1px solid var(--seller-border, #e5e7eb)',
                        borderRadius: 18,
                        padding: 24,
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                    }}>
                        <h3 style={{ fontSize: 17, fontWeight: 700, color: '#111827', margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <DollarSign size={18} color="#FE2C55" />
                            <span>{rtl ? 'التسعير والعملة للمستهلك' : 'Retail Pricing & Currency'}</span>
                        </h3>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                            <div>
                                <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
                                    {rtl ? 'سعر البيع الأساسي *' : 'Selling Price *'}
                                </label>
                                <input
                                    type="number"
                                    placeholder="2500"
                                    value={formData.price}
                                    onChange={e => setFormData(prev => ({ ...prev, price: e.target.value }))}
                                    style={{
                                        width: '100%',
                                        padding: '12px 14px',
                                        borderRadius: 10,
                                        background: '#ffffff',
                                        border: '1px solid #D1D5DB',
                                        color: '#FE2C55',
                                        fontSize: 16,
                                        fontWeight: 800,
                                        outline: 'none',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
                                    {rtl ? 'السعر قبل الخصم (اختياري)' : 'Compare-At Price (Discount)'}
                                </label>
                                <input
                                    type="number"
                                    placeholder="3200"
                                    value={formData.compareAtPrice}
                                    onChange={e => setFormData(prev => ({ ...prev, compareAtPrice: e.target.value }))}
                                    style={{
                                        width: '100%',
                                        padding: '12px 14px',
                                        borderRadius: 10,
                                        background: '#ffffff',
                                        border: '1px solid #D1D5DB',
                                        color: '#6B7280',
                                        fontSize: 15,
                                        outline: 'none',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
                                    {rtl ? 'العملة' : 'Currency'}
                                </label>
                                <select
                                    value={formData.currency}
                                    onChange={e => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                                    style={{
                                        width: '100%',
                                        padding: '12px 14px',
                                        borderRadius: 10,
                                        background: '#ffffff',
                                        border: '1px solid #D1D5DB',
                                        color: '#111827',
                                        fontSize: 14,
                                        outline: 'none',
                                        boxSizing: 'border-box'
                                    }}
                                >
                                    <option value="SAR">SAR (ريال سعودي 🇸🇦)</option>
                                    <option value="AED">AED (درهم إماراتي 🇦🇪)</option>
                                    <option value="USD">USD (دولار أمريكي 🇺🇸)</option>
                                    <option value="SOS">SOS (شلن صومالي 🇸🇴)</option>
                                    <option value="EUR">EUR (يورو 🇪🇺)</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Wholesale & B2B (If Applicable) */}
                    {(formData.audience === 'WHOLESALE' || formData.audience === 'BOTH') && (
                        <div style={{
                            background: '#FFFBEB',
                            border: '1px solid #FDE68A',
                            borderRadius: 18,
                            padding: 24,
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                        }}>
                            <h3 style={{ fontSize: 17, fontWeight: 700, color: '#D97706', margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Package size={18} color="#D97706" />
                                <span>{rtl ? 'أسعار الجملة والتوريد (Wholesale Rules)' : 'B2B Wholesale Rules'}</span>
                            </h3>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#78350F', marginBottom: 6 }}>
                                        {rtl ? 'سعر الجملة للقطعة' : 'Wholesale Unit Price'}
                                    </label>
                                    <input
                                        type="number"
                                        placeholder="1800"
                                        value={formData.wholesalePrice}
                                        onChange={e => setFormData(prev => ({ ...prev, wholesalePrice: e.target.value }))}
                                        style={{
                                            width: '100%',
                                            padding: '11px 14px',
                                            borderRadius: 10,
                                            background: '#ffffff',
                                            border: '1px solid #FCD34D',
                                            color: '#B45309',
                                            fontWeight: 700,
                                            fontSize: 14,
                                            outline: 'none',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#78350F', marginBottom: 6 }}>
                                        {rtl ? 'أقل كمية طلب للجملة (MOQ)' : 'Minimum Order Qty (MOQ)'}
                                    </label>
                                    <input
                                        type="number"
                                        placeholder="10"
                                        value={formData.moq}
                                        onChange={e => setFormData(prev => ({ ...prev, moq: e.target.value }))}
                                        style={{
                                            width: '100%',
                                            padding: '11px 14px',
                                            borderRadius: 10,
                                            background: '#ffffff',
                                            border: '1px solid #D1D5DB',
                                            color: '#111827',
                                            fontSize: 14,
                                            outline: 'none',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#78350F', marginBottom: 6 }}>
                                        {rtl ? 'عدد القطع في الكرتون' : 'Units Per Carton'}
                                    </label>
                                    <input
                                        type="number"
                                        placeholder="24"
                                        value={formData.unitsPerCarton}
                                        onChange={e => setFormData(prev => ({ ...prev, unitsPerCarton: e.target.value }))}
                                        style={{
                                            width: '100%',
                                            padding: '11px 14px',
                                            borderRadius: 10,
                                            background: '#ffffff',
                                            border: '1px solid #D1D5DB',
                                            color: '#111827',
                                            fontSize: 14,
                                            outline: 'none',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Stock & Logistics */}
                    <div style={{
                        background: 'var(--seller-card, #ffffff)',
                        border: '1px solid var(--seller-border, #e5e7eb)',
                        borderRadius: 18,
                        padding: 24,
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                    }}>
                        <h3 style={{ fontSize: 17, fontWeight: 700, color: '#111827', margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Truck size={18} color="#FE2C55" />
                            <span>{rtl ? 'المخزون ومعايير الشحن' : 'Inventory & Shipping Metrics'}</span>
                        </h3>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                            <div>
                                <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
                                    {rtl ? 'إجمالي المخزون المتاح (قطعة)' : 'Total Stock (Units)'}
                                </label>
                                <input
                                    type="number"
                                    value={formData.masterStock}
                                    onChange={e => setFormData(prev => ({ ...prev, masterStock: e.target.value }))}
                                    style={{
                                        width: '100%',
                                        padding: '11px 14px',
                                        borderRadius: 10,
                                        background: '#ffffff',
                                        border: '1px solid #D1D5DB',
                                        color: '#10B981',
                                        fontWeight: 700,
                                        fontSize: 15,
                                        outline: 'none',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
                                    {rtl ? 'الوزن التقريبي (كجم)' : 'Approx Weight (kg)'}
                                </label>
                                <input
                                    type="number"
                                    step="0.1"
                                    placeholder="0.8"
                                    value={formData.weight}
                                    onChange={e => setFormData(prev => ({ ...prev, weight: e.target.value }))}
                                    style={{
                                        width: '100%',
                                        padding: '11px 14px',
                                        borderRadius: 10,
                                        background: '#ffffff',
                                        border: '1px solid #D1D5DB',
                                        color: '#111827',
                                        fontSize: 14,
                                        outline: 'none',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
                                    {rtl ? 'فئة الشحن' : 'Shipping Class'}
                                </label>
                                <select
                                    value={formData.shippingClass}
                                    onChange={e => setFormData(prev => ({ ...prev, shippingClass: e.target.value }))}
                                    style={{
                                        width: '100%',
                                        padding: '11px 14px',
                                        borderRadius: 10,
                                        background: '#ffffff',
                                        border: '1px solid #D1D5DB',
                                        color: '#111827',
                                        fontSize: 14,
                                        outline: 'none',
                                        boxSizing: 'border-box'
                                    }}
                                >
                                    <option value="STANDARD">{rtl ? 'شحن قياسي (Standard)' : 'Standard Delivery'}</option>
                                    <option value="VIP_EXPRESS">{rtl ? 'شحن سريع VIP (Express)' : 'VIP Express'}</option>
                                    <option value="HEAVY">{rtl ? 'بضائع ثقيلة (Heavy Freight)' : 'Heavy Freight'}</option>
                                    <option value="TEMPERATURE_CONTROLLED">{rtl ? 'شحن مبرد / عطور حساسة' : 'Temperature Controlled'}</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ═════════════════════════════════════════════════════════════
                STAGE 6: REVIEW, LIVE PREVIEW & PUBLISH (المعاينة والاعتماد)
               ═════════════════════════════════════════════════════════════ */}
            {currentStep === 6 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                    
                    {/* Live Preview Card */}
                    <div style={{
                        background: 'var(--seller-card, #ffffff)',
                        border: '1px solid var(--seller-border, #e5e7eb)',
                        borderRadius: 18,
                        padding: 24,
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                            <h3 style={{ fontSize: 17, fontWeight: 700, color: '#111827', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Eye size={18} color="#FE2C55" />
                                <span>{rtl ? 'المعاينة الحية للمنتج في المتجر' : 'Live Storefront Card Preview'}</span>
                            </h3>
                            <span style={{ fontSize: 12, color: '#FE2C55', background: '#FFF0F3', padding: '4px 10px', borderRadius: 20, fontWeight: 700 }}>
                                {rtl ? 'كيف سيظهر للمشتري' : 'Buyer Preview'}
                            </span>
                        </div>

                        {/* Storefront Card Simulation */}
                        <div style={{ maxWidth: 360, margin: '0 auto', background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 20, overflow: 'hidden', boxShadow: '0 12px 30px rgba(0, 0, 0, 0.08)' }}>
                            <div style={{ position: 'relative', height: 260, background: '#f3f4f6' }}>
                                {formData.coverImageUrl ? (
                                    <img src={resolveStorefrontImageUrl(formData.coverImageUrl)} alt="Card Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Package size={48} color="#9ca3af" />
                                    </div>
                                )}
                                <div style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(255, 255, 255, 0.95)', color: '#111827', padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
                                    {formData.categoryName}
                                </div>
                                {formData.compareAtPrice && parseFloat(formData.compareAtPrice) > parseFloat(formData.price) && (
                                    <div style={{ position: 'absolute', top: 12, left: 12, background: '#FE2C55', color: '#ffffff', padding: '4px 8px', borderRadius: 6, fontSize: 11, fontWeight: 800 }}>
                                        {rtl ? `خصم ${Math.round(((formData.compareAtPrice - formData.price) / formData.compareAtPrice) * 100)}%` : 'SALE'}
                                    </div>
                                )}
                            </div>

                            <div style={{ padding: 18 }}>
                                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>
                                    {formData.brand || 'LUXVONO'}
                                </div>
                                <h4 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: '0 0 10px 0', lineHeight: 1.4 }}>
                                    {formData.nameAr || formData.nameEn || 'Luxury Product'}
                                </h4>

                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
                                    <div>
                                        <div style={{ fontSize: 18, fontWeight: 800, color: '#FE2C55' }}>
                                            {formData.price} {formData.currency}
                                        </div>
                                        {formData.compareAtPrice && (
                                            <div style={{ fontSize: 12, color: '#9ca3af', textDecoration: 'line-through' }}>
                                                {formData.compareAtPrice} {formData.currency}
                                            </div>
                                        )}
                                    </div>

                                    {/* Color dots preview */}
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        {formData.selectedColors.slice(0, 4).map((c, i) => (
                                            <span key={i} style={{ width: 12, height: 12, borderRadius: '50%', background: c.hex, border: '1px solid #d1d5db' }} />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Summary Specifications Grid */}
                    <div style={{
                        background: 'var(--seller-card, #ffffff)',
                        border: '1px solid var(--seller-border, #e5e7eb)',
                        borderRadius: 18,
                        padding: 24,
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                    }}>
                        <h3 style={{ fontSize: 17, fontWeight: 700, color: '#111827', margin: '0 0 16px 0' }}>
                            {rtl ? 'ملخص بيانات الاعتماد' : 'Moderation Submission Summary'}
                        </h3>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
                            <div style={{ background: '#F9FAFB', border: '1px solid #e5e7eb', padding: 14, borderRadius: 12 }}>
                                <div style={{ fontSize: 12, color: '#6B7280' }}>{rtl ? 'الأسواق المستهدفة' : 'Target Markets'}</div>
                                <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginTop: 4 }}>
                                    {formData.isWorldwideMarket ? 'عالمياً 🌍' : `${formData.targetMarkets.join(', ')} 🌐`}
                                </div>
                            </div>

                            <div style={{ background: '#F9FAFB', border: '1px solid #e5e7eb', padding: 14, borderRadius: 12 }}>
                                <div style={{ fontSize: 12, color: '#6B7280' }}>{rtl ? 'المخزون الكلي' : 'Total Inventory'}</div>
                                <div style={{ fontSize: 14, fontWeight: 700, color: '#10b981', marginTop: 4 }}>
                                    {formData.masterStock} {rtl ? 'قطعة' : 'Units'}
                                </div>
                            </div>

                            <div style={{ background: '#F9FAFB', border: '1px solid #e5e7eb', padding: 14, borderRadius: 12 }}>
                                <div style={{ fontSize: 12, color: '#6B7280' }}>{rtl ? 'عدد خيارات الألوان والمقاسات' : 'Generated Variants'}</div>
                                <div style={{ fontSize: 14, fontWeight: 700, color: '#FE2C55', marginTop: 4 }}>
                                    {formData.variants.length} {rtl ? 'خيارات' : 'Variants'}
                                </div>
                            </div>

                            <div style={{ background: '#F9FAFB', border: '1px solid #e5e7eb', padding: 14, borderRadius: 12 }}>
                                <div style={{ fontSize: 12, color: '#6B7280' }}>{rtl ? 'رمز SKU الرئيسي' : 'Master SKU'}</div>
                                <div style={{ fontSize: 14, fontWeight: 700, color: '#6366F1', marginTop: 4 }}>
                                    {formData.sku}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Moderation Status Card */}
                    <div style={{
                        background: '#FFFBEB',
                        border: '1px solid #FDE68A',
                        borderRadius: 18,
                        padding: 24,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 16
                    }}>
                        <div style={{
                            width: 44,
                            height: 44,
                            borderRadius: '50%',
                            background: '#FEF3C7',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                        }}>
                            <ShieldCheck size={24} color="#D97706" />
                        </div>
                        <div>
                            <div style={{ fontSize: 12, color: '#B45309', fontWeight: 800, textTransform: 'uppercase', marginBottom: 4 }}>
                                🟠 {rtl ? 'قيد مراجعة الجودة والاعتماد (PENDING_REVIEW)' : 'PENDING REVIEW'}
                            </div>
                            <h4 style={{ fontSize: 16, fontWeight: 700, color: '#1F2937', margin: '0 0 6px 0' }}>
                                {rtl ? 'اعتماد المنشور وإرساله للمراجعة 🚀' : 'Product Moderation & Approval'}
                            </h4>
                            <p style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.6, margin: 0 }}>
                                {rtl 
                                    ? 'سيتم إرسال المنشور للمراجعة من قبل فريق الجودة ومكافحة التزييف لضمان مطابقة الصور والأسعار للمعايير. بمجرد الاعتماد من لوحة التحكم سيصلك إشعار فوري وسيصبح متاحاً للجمهور مباشرة.'
                                    : 'The product will be submitted for compliance review and published instantly upon moderation approval.'}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Fixed Bottom Actions Footer ─────────────────────────── */}
            <div className="wizard-bottom-bar" style={{ direction: rtl ? 'rtl' : 'ltr' }}>
                <div>
                    {currentStep > 1 ? (
                        <button
                            type="button"
                            onClick={handleBack}
                            className="wizard-btn-back"
                        >
                            {rtl ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
                            <span>{rtl ? 'السابق' : 'Back'}</span>
                        </button>
                    ) : (
                        <div />
                    )}
                </div>

                <div className="wizard-bottom-actions-right">
                    <button
                        type="button"
                        onClick={handleSaveDraft}
                        className="wizard-btn-draft"
                    >
                        <Save size={16} />
                        <span>{rtl ? 'حفظ مسودة' : 'Save Draft'}</span>
                    </button>

                    {currentStep < 6 ? (
                        <button
                            type="button"
                            onClick={handleNext}
                            className="wizard-btn-next"
                        >
                            <span>{rtl ? 'المرحلة التالية' : 'Next Step'}</span>
                            {rtl ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
                        </button>
                    ) : (
                        <button
                            type="button"
                            disabled={isSubmitting}
                            onClick={handleSubmitForReview}
                            className="wizard-btn-submit"
                            style={{ opacity: isSubmitting ? 0.7 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                        >
                            {isSubmitting ? (
                                <>
                                    <Spinner size={16} />
                                    <span>{uploadStatusText || (rtl ? 'جاري الإرسال...' : 'Submitting...')}</span>
                                </>
                            ) : (
                                <>
                                    <Rocket size={18} />
                                    <span>{rtl ? 'إرسال للمراجعة والاعتماد 🚀' : 'Submit for Review 🚀'}</span>
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
